import { useState, useEffect, useCallback } from "react";

// ─── REAL RACE DATA ──────────────────────────────────────────────────────────
const STATIONS = [
  {
    id: "ski", name: "Ski Erg", emoji: "",
    spec: { open: "1,000m", women: "1,000m", pro: "1,000m", doubles: "1,000m" },
    inputUnit: "/500m split", inputHint: "500m split pace", placeholder: "215",
    avg: { open: 280, women: 310, pro: 257, doubles: 270 },
    elite: { open: 227, women: 262, pro: 200, doubles: 240 },
    training: {
      focus: "Aerobic power + pacing consistency",
      workouts: [
        "4 × 250m @ race effort, 90s rest — build pace consistency",
        "2 × 500m @ 5s faster than goal split, 3min rest",
        "1000m time trial weekly — track your benchmark directly",
        "10min easy ski erg at conversational pace — aerobic base"
      ],
      tip: "Drive with your hips, not just arms. Most beginners lose 20–30s from poor posture alone."
    },
    calc: (val) => { const t = parseMMSS(val); if (t) return t * 2; return null; }
  },
  {
    id: "sled_push", name: "Sled Push", emoji: "",
    spec: { open: "50m · +102kg", women: "50m · +68kg", pro: "50m · +152kg", doubles: "50m · standard" },
    inputUnit: "mm:ss", inputHint: "estimate your time", placeholder: "330",
    avg: { open: 225, women: 245, pro: 220, doubles: 220 },
    elite: { open: 165, women: 180, pro: 140, doubles: 165 },
    training: {
      focus: "Leg drive + forward lean mechanics",
      workouts: [
        "6 × 25m sled push at race weight, 2min rest — race simulation",
        "4 × 50m at lighter weight (70%) focusing on continuous movement",
        "Heavy leg press 4×8 — build raw pushing strength",
        "Walking lunges 3×20 — posterior chain endurance"
      ],
      tip: "Stay low and keep moving — stopping to rest costs far more time than slowing down does."
    },
    calc: (val) => { const t = parseMMSS(val); return t || null; }
  },
  {
    id: "sled_pull", name: "Sled Pull", emoji: "",
    spec: { open: "50m · +78kg", women: "50m · +48kg", pro: "50m · +103kg", doubles: "50m · standard" },
    inputUnit: "mm:ss", inputHint: "estimate your time", placeholder: "530",
    avg: { open: 375, women: 410, pro: 391, doubles: 380 },
    elite: { open: 194, women: 234, pro: 180, doubles: 210 },
    training: {
      focus: "Back strength + grip endurance",
      workouts: [
        "6 × 25m rope pull at race weight — most specific drill possible",
        "Seated cable rows 4×12 — lat and mid-back endurance",
        "Farmer carries 4×40m — grip and pulling posture",
        "Band pull-aparts 3×20 — rear delt and upper back health"
      ],
      tip: "The sled pull has the largest gap between average and elite of any station. Most athletes are shocked mid-race. Train it more than you think you need to."
    },
    calc: (val) => { const t = parseMMSS(val); return t || null; }
  },
  {
    id: "burpee", name: "Burpee Broad Jump", emoji: "",
    spec: { open: "80m", women: "80m", pro: "80m", doubles: "80m" },
    inputUnit: "mm:ss", inputHint: "estimate your time", placeholder: "500",
    avg: { open: 315, women: 300, pro: 315, doubles: 300 },
    elite: { open: 141, women: 191, pro: 130, doubles: 170 },
    training: {
      focus: "Jump distance + sustainable pace",
      workouts: [
        "3 × 20 burpee broad jumps — practice race mechanics",
        "Max distance broad jump sets 5×5 — improve per-rep distance",
        "80m time trial weekly — measure improvement directly",
        "Box jumps 4×8 — explosive hip extension"
      ],
      tip: "Every extra inch of jump distance saves a rep. A 6ft vs 5ft jump saves ~16 reps over 80m — roughly 60–90 seconds."
    },
    calc: (val) => { const t = parseMMSS(val); return t || null; }
  },
  {
    id: "rowing", name: "Rowing", emoji: "",
    spec: { open: "1,000m total", women: "1,000m total", pro: "1,000m total", doubles: "1,000m total" },
    inputUnit: "/500m split", inputHint: "your 500m split pace", placeholder: "215",
    avg: { open: 285, women: 325, pro: 275, doubles: 280 },
    elite: { open: 238, women: 269, pro: 220, doubles: 250 },
    training: {
      focus: "Aerobic engine + early pacing discipline",
      workouts: [
        "4 × 500m @ 5s faster than goal split, 2min rest",
        "2 × 1000m at race pace — full station simulation",
        "20min steady row at easy pace — aerobic base",
        "10 × 100m sprint with 45s rest — power development"
      ],
      tip: "Don't sprint the first 200m. Start at race pace and hold it. Most athletes blow up the row by going out too hard."
    },
    calc: (val) => { const t = parseMMSS(val); if (t) return t * 2; return null; }
  },
  {
    id: "farmers", name: "Farmer's Carry", emoji: "",
    spec: { open: "200m · 2×24kg", women: "200m · 2×16kg", pro: "200m · 2×32kg", doubles: "200m split" },
    inputUnit: "mm:ss", inputHint: "estimate your time", placeholder: "230",
    avg: { open: 160, women: 180, pro: 152, doubles: 155 },
    elite: { open: 97, women: 117, pro: 90, doubles: 105 },
    training: {
      focus: "Grip strength + walking cadence",
      workouts: [
        "4 × 50m at race weight, rest 90s — pace practice",
        "2 × 200m at lighter weight (80%) non-stop",
        "Dead hangs 3×max — grip endurance",
        "Trap bar carries 3×40m heavy — strength base"
      ],
      tip: "Set a walking cadence before you pick up the weights and maintain it. Stopping to re-grip costs 10–20s each time."
    },
    calc: (val) => { const t = parseMMSS(val); return t || null; }
  },
  {
    id: "sandbag", name: "Sandbag Lunges", emoji: "",
    spec: { open: "100m · 20kg", women: "100m · 10kg", pro: "100m · 30kg", doubles: "100m split" },
    inputUnit: "mm:ss", inputHint: "estimate your time", placeholder: "500",
    avg: { open: 330, women: 355, pro: 322, doubles: 330 },
    elite: { open: 183, women: 215, pro: 170, doubles: 200 },
    training: {
      focus: "Quad endurance + balance under fatigue",
      workouts: [
        "4 × 25m sandbag lunges at race weight, 2min rest",
        "2 × 100m bodyweight lunges for pace and mechanics",
        "Bulgarian split squats 3×10 each leg — single leg strength",
        "Walking lunges with DBs 3×20 — endurance under load"
      ],
      tip: "Station 7 hits after 5 runs and 6 stations. Your legs will be wrecked. Train lunges when already fatigued — do them at the end of workouts, never fresh."
    },
    calc: (val) => { const t = parseMMSS(val); return t || null; }
  },
  {
    id: "wallball", name: "Wall Balls", emoji: "",
    spec: { open: "100 reps · 6kg", women: "100 reps · 4kg", pro: "100 reps · 9kg", doubles: "100 reps split" },
    inputUnit: "rpm", inputHint: "reps per minute", placeholder: "18",
    avg: { open: 480, women: 420, pro: 458, doubles: 460 },
    elite: { open: 265, women: 282, pro: 250, doubles: 270 },
    training: {
      focus: "Pace management + breathing rhythm",
      workouts: [
        "3 × 30 reps unbroken, rest 2min — build unbroken capacity",
        "100 rep time trial weekly — track improvement directly",
        "5 × 20 reps with 45s rest — high volume tolerance",
        "Thrusters 4×10 — closest movement pattern to wall balls"
      ],
      tip: "Wall balls are the final station. Your lungs will be on fire. Practice sets of 20–25 unbroken and know your sustainable pace cold before race day."
    },
    calc: (val) => {
      const rpm = parseFloat(val);
      if (rpm && rpm > 0) return Math.round(100 / rpm * 60);
      return null;
    }
  }
];

const FATIGUE = [1.0, 1.08, 1.15, 1.13, 1.15, 1.13, 1.16, 1.25];
const ROXZONE = 418;

const DIVISIONS = [
  { id:"open",    label:"Open",    sub:"All athletes" },
  { id:"pro",     label:"Pro",     sub:"Qualifier required" },
  { id:"doubles", label:"Doubles", sub:"Team of 2" },
  { id:"relay",   label:"Relay",   sub:"Team of 4" },
];

const GENDERS = [
  { id:"male",   label:"Male" },
  { id:"female", label:"Female" },
];

const AGE_BRACKETS = [
  { id:"16",  label:"16–29",  min:16, max:29 },
  { id:"30",  label:"30–39",  min:30, max:39 },
  { id:"40",  label:"40–49",  min:40, max:49 },
  { id:"50",  label:"50–59",  min:50, max:59 },
  { id:"60",  label:"60+",    min:60, max:99 },
];

// Age multipliers — older brackets average slightly slower
// Based on real HYROX masters data: each decade ~4-7% slower
const AGE_MULT = { "16":1.0, "30":1.03, "40":1.08, "50":1.14, "60":1.22 };

// Gender multipliers on station times (male = 1.0 baseline)
const GENDER_MULT = { male:1.0, female:1.10 };

// Run pace defaults per division+gender (seconds/km)
const RUN_DEFAULTS = {
  open:    { male:320, female:365 },
  pro:     { male:231, female:268 },
  doubles: { male:330, female:370 },
  relay:   { male:330, female:370 },
};

// DIV_LABELS for display
const DIV_LABELS = { open:"Open", pro:"Pro", doubles:"Doubles", relay:"Relay" };

// Tiers by total finish time (male open baseline)
const TIERS = [
  { label:"Building",     min:6000, max:Infinity, color:"#aaaaaa" },
  { label:"Average",      min:5400, max:6000,     color:"#ffaa00" },
  { label:"Advanced",     min:4800, max:5400,     color:"#00aaff" },
  { label:"Competitive",  min:4200, max:4800,     color:"#aadd00" },
  { label:"Podium",       min:0,    max:4200,     color:"#00e87a" },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function parseMMSS(val) {
  if (!val) return null;
  const s = String(val).trim();
  const parts = s.split(":");
  if (parts.length === 2) {
    const m = parseInt(parts[0]), sec = parseInt(parts[1]);
    if (!isNaN(m) && !isNaN(sec)) return m * 60 + sec;
  }
  const n = parseFloat(s);
  if (!isNaN(n)) return Math.round(n * 60);
  return null;
}
function parseFinishTime(val) {
  // HYROX times: "1:25:30" = 1h 25m 30s, "1:25" = 1h 25m, "85" = 85 minutes
  if (!val) return 0;
  const s = val.trim().replace(/[^0-9:]/g, '');
  if (!s) return 0;
  const parts = s.split(':').map(p => parseInt(p) || 0);
  if (parts.length === 3) return parts[0]*3600 + parts[1]*60 + parts[2];
  if (parts.length === 2) return parts[0]*3600 + parts[1]*60; // always h:mm
  // Single number: if >= 60 treat as minutes, else as hours (e.g. "1" = 1 hour)
  const n = parseInt(s) || 0;
  return n >= 60 ? n * 60 : n * 3600;
}
function fmt(sec) {
  if (sec == null || isNaN(sec)) return "--:--";
  const m = Math.floor(Math.abs(sec) / 60), s = Math.round(Math.abs(sec) % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
function fmtLong(sec) {
  if (sec == null || isNaN(sec)) return "--:--";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = Math.round(sec % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2,"0")}:${s.toString().padStart(2,"0")}`;
  return `${m}:${s.toString().padStart(2,"0")}`;
}
function getBenchmarks(div, gender, age) {
  // Base avgs/elites are male open 16-29.
  // Apply gender + age multipliers to get realistic division benchmarks.
  const gm = GENDER_MULT[gender] || 1.0;
  const am = AGE_MULT[age] || 1.0;
  // Pro athletes are faster — apply a pro discount
  const dm = div === "pro" ? 0.88 : 1.0;
  // Doubles/relay — team stations are split, slightly faster per person
  const tm = (div === "doubles" || div === "relay") ? 0.97 : 1.0;
  const mult = gm * am * dm * tm;
  return STATIONS.map(s => ({
    avg:   Math.round(s.avg.open  * mult),
    elite: Math.round(s.elite.open * mult),
  }));
}

function getStationSpec(s, div, gender) {
  // Return the correct weight spec for this division+gender
  const key = div === "pro" ? (gender === "female" ? "women" : "pro")
             : div === "doubles" ? "doubles"
             : div === "relay"   ? "doubles"
             : (gender === "female" ? "women" : "open");
  return s.spec[key] || s.spec.open;
}

function calcRace(inputs, runPace, profile) {
  const { div, gender, age } = profile;
  const km = runPace || RUN_DEFAULTS[div][gender];
  const runs = FATIGUE.map(f => Math.round(km * f));
  const benchmarks = getBenchmarks(div, gender, age);
  const stations = STATIONS.map((s, i) => {
    const t = s.calc(inputs[s.id]);
    return t != null ? t : benchmarks[i].avg;
  });
  const totalRun = runs.reduce((a,b)=>a+b,0);
  const totalStation = stations.reduce((a,b)=>a+b,0);
  const total = totalRun + totalStation + ROXZONE;
  // Run analytics
  const runPct = Math.round(totalRun / total * 100);
  const stationPct = 100 - runPct;
  // Per-run split targets with pacing notes
  const runTargets = FATIGUE.map((f, i) => ({
    run: i + 1,
    time: Math.round(km * f),
    note: i === 0 ? "Start controlled" : i === 7 ? "Final push" : i >= 5 ? "Hold form" : "Race pace"
  }));
  // Elite run pace for this profile
  const am2 = AGE_MULT[age] || 1.0;
  const dm2 = div === "pro" ? 0.88 : 1.0;
  const eliteKm = Math.round(RUN_DEFAULTS[div][gender] * 0.78 * am2 * dm2);
  return { runs, stations, totalRun, totalStation, total, benchmarks, runPct, stationPct, km, eliteKm, runTargets };
}

function calcRunImpact(currentKm, improveBy) {
  const fasterKm = Math.max(180, currentKm - improveBy);
  const curr = FATIGUE.map(f => Math.round(currentKm * f)).reduce((a,b)=>a+b,0);
  const fast = FATIGUE.map(f => Math.round(fasterKm * f)).reduce((a,b)=>a+b,0);
  return { saved: curr - fast, fasterKm };
}
function calcReadiness(result, inputs) {
  const bms = result.benchmarks;

  // Component 1: Station performance (75pts)
  // Where does each station sit between avg (0) and elite (1)?
  // Below avg is negative, capped at -0.5 so one bad station doesn't crater everything
  const stationScores = STATIONS.map((s, i) => {
    const sec = result.stations[i];
    const { avg, elite } = bms[i];
    const range = avg - elite;
    const pos = range > 0 ? (avg - sec) / range : 0;
    return Math.min(1, Math.max(-0.5, pos));
  });
  const avgScore = stationScores.reduce((a, b) => a + b, 0) / stationScores.length;
  // Map [-0.5, 1.0] to [0, 75]
  const stationComponent = Math.round(Math.max(0, Math.min(75, ((avgScore + 0.5) / 1.5) * 75)));

  // Component 2: Data quality (25pts)
  // Real benchmarks vs estimates — more real data = more trustworthy score
  const userFilled = Object.values(inputs).filter(v => v && v.toString().trim()).length;
  const dataComponent = Math.round((userFilled / 8) * 25);

  const total = stationComponent + dataComponent;
  return {
    score: total,
    stationComponent,
    dataComponent,
    label: total >= 80 ? "Performing" : total >= 60 ? "On Track" : total >= 40 ? "Developing" : "Getting Started",
    color: total >= 80 ? "#00e87a" : total >= 60 ? "#aadd00" : total >= 40 ? "#ffaa00" : "#ff6b50",
    description: total >= 80
      ? "Your station performance is strong across the board. Keep building on this base."
      : total >= 60
      ? "Solid foundation. Target your weakest stations to move up the performance bands."
      : total >= 40
      ? "Good start. Add your real station benchmarks for a more accurate picture."
      : "Enter your real station times to unlock a meaningful performance breakdown."
  };
}

function getTier(sec) { return TIERS.find(t => sec >= t.min && sec < t.max) || TIERS[0]; }
function getGap(sec, elite, avg) {
  const range = avg - elite, pos = sec - elite;
  const pct = Math.min(100, Math.max(0, range > 0 ? (pos / range) * 100 : 50));
  if (pct < 25) return { label: "Podium",      color: "#00e87a" };
  if (pct < 50) return { label: "Competitive", color: "#aadd00" };
  if (pct < 70) return { label: "Advanced",    color: "#00aaff" };
  if (pct < 88) return { label: "Average",     color: "#ffaa00" };
  return { label: "Building", color: "#ff3c00" };
}

// ─── HELPER FUNCTIONS ────────────────────────────────────────────────────────
function clamp(n, min, max) { // eslint-disable-line no-unused-vars
  return Math.max(min, Math.min(max, n));
}
function round2(n) {
  return Math.round(n * 100) / 100;
}
// Robust station name → id resolver. Supersedes STATION_NAME_TO_ID lookup table.
function getStationIdFromName(name) {
  const s = String(name || "").toLowerCase();
  if (s.includes("ski"))        return "ski";
  if (s.includes("sled push"))  return "sled_push";
  if (s.includes("sled pull"))  return "sled_pull";
  if (s.includes("burpee"))     return "burpee";
  if (s.includes("row"))        return "rowing";
  if (s.includes("farmer"))     return "farmers";
  if (s.includes("sandbag"))    return "sandbag";
  if (s.includes("wall"))       return "wallball";
  return null;
}
// Returns "high" / "medium" / "low" based on how many stations the user entered manually.
// Used to calibrate AI prompt specificity — high-confidence data warrants more precise prescriptions.
function getDataConfidence(inputs = {}) {
  const enteredStationCount = Object.values(inputs).filter(v => v && String(v).trim()).length;
  if (enteredStationCount >= 6) return "high";
  if (enteredStationCount >= 3) return "medium";
  return "low";
}
// Derives training programming parameters from the athlete's time gap to target and level.
// Shapes the AI plan's session complexity, focus, and week-to-week progression arc.
function getAthleteLevelFromTier(tierLabel) {
  if (tierLabel === "Podium" || tierLabel === "Competitive") return "Advanced";
  if (tierLabel === "Advanced" || tierLabel === "Average") return "Intermediate";
  return "Beginner";
}
function getTrainingBias(timeGapPercent, athleteLevel) {
  if (timeGapPercent == null) {
    return {
      focus: "balanced threshold development",
      complexity: athleteLevel === "Beginner" ? "low" : "moderate",
      density_progression: "gradual"
    };
  }
  if (timeGapPercent > 10) {
    return {
      focus: "aerobic threshold support and foundational hybrid durability",
      complexity: "low",
      density_progression: "conservative"
    };
  }
  if (timeGapPercent > 5) {
    return {
      focus: "balanced threshold development with integrated station weaknesses",
      complexity: "moderate",
      density_progression: "moderate"
    };
  }
  return {
    focus: "race-specific threshold density and transition efficiency",
    complexity: athleteLevel === "Advanced" ? "high" : "moderate",
    density_progression: "aggressive"
  };
}

// ─── THRESHOLD TRAINING CONSTANTS ────────────────────────────────────────────
// Per-station limitation weights: how much each station tests each physical quality.
// Used by deriveAthleteProfile to compute a weighted limitation score.
const LIMITATION_WEIGHTS = {
  ski:       { aerobic_threshold_support:0.55, upper_pull_posterior_endurance:0.20, race_specific_recovery_into_running:0.25 },
  sled_push: { lower_body_fatigue_resistance:0.65, race_specific_recovery_into_running:0.35 },
  sled_pull: { upper_pull_posterior_endurance:0.40, grip_trunk_endurance:0.30, race_specific_recovery_into_running:0.30 },
  burpee:    { lower_body_fatigue_resistance:0.30, aerobic_threshold_support:0.20, race_specific_recovery_into_running:0.50 },
  rowing:    { aerobic_threshold_support:0.60, upper_pull_posterior_endurance:0.20, race_specific_recovery_into_running:0.20 },
  farmers:   { grip_trunk_endurance:0.70, race_specific_recovery_into_running:0.30 },
  sandbag:   { lower_body_fatigue_resistance:0.60, grip_trunk_endurance:0.15, race_specific_recovery_into_running:0.25 },
  wallball:  { aerobic_threshold_support:0.25, lower_body_fatigue_resistance:0.25, race_specific_recovery_into_running:0.50 },
};
// Amplifies a station's limitation signal based on race position fatigue.
// Later stations (sandbag 1.15, wallball 1.18) cost more because they hit when the athlete is already depleted.
const RACE_POSITION_MULTIPLIER = {
  ski:1.00, sled_push:1.08, sled_pull:1.03, burpee:1.05,
  rowing:1.00, farmers:1.05, sandbag:1.15, wallball:1.18,
};
// Human-readable coaching language for each limitation key, used in the AI prompt.
const LIMITATION_LABELS = {
  aerobic_threshold_support:             "aerobic threshold capacity limiting sustainable race pace",
  lower_body_fatigue_resistance:         "lower body fatigue resistance under repeated threshold efforts",
  upper_pull_posterior_endurance:        "upper body pulling and posterior-chain endurance under fatigue",
  grip_trunk_endurance:                  "grip and trunk endurance under accumulated fatigue",
  race_specific_recovery_into_running:   "recovery into running after stations is inefficient and costly",
};
// Station display name → id lookup (handles edge cases like "Wall Balls" → "wallball")


// ─── HOOKS ───────────────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ─── STORAGE ─────────────────────────────────────────────────────────────────
async function loadEntries() {
  try { const r = await window.storage.get("splitrox:v2:entries"); return r ? JSON.parse(r.value) : []; }
  catch { return []; }
}
async function saveEntries(entries) {
  try { await window.storage.set("splitrox:v2:entries", JSON.stringify(entries)); }
  catch(e) { console.error("Storage error", e); }
}

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const G = {
  bgInput:"#1C1C1E", bgResults:"#0C0C0C", bgPlan:"#F5F3EF", bgNav:"#1A1A1C",
  card:"#242426", cardDeep:"#181818", border:"#303032", borderTop:"#3A3A3C", borderDark:"#222222",
  accent:"#F2620F", accentDark:"#C44E0A", accentLight:"#FF8040",
  white:"#FFFFFF", textBody:"#CCCCCC", textMuted:"#888888", textDim:"#555555",
  good:"#00b860", goodDim:"#78b900", warn:"#dc9600", danger:"#F2620F",
  planText:"#0C0C0C", planMuted:"#666666",
  black:"#0C0C0C", dark:"#1A1A1C", muted:"#888888",
};

// ─── BASE COMPONENTS ─────────────────────────────────────────────────────────
function SLabel({ children, style={}, muted=false }) {
  const color = muted ? "#888888" : "#F2620F";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.3em", textTransform:"uppercase", color, marginBottom:10, ...style }}>
      <span style={{ display:"inline-block", width:16, height:1, background:color, flexShrink:0 }} />
      {children}
    </div>
  );
}
function SInput({ value, onChange, placeholder, style={}, timeInput=false, hmsInput=false, ...rest }) {
  const [focused, setFocused] = useState(false);

  const handleTimeChange = (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    if (digits.length === 0) { onChange(""); return; }
    if (digits.length <= 2) {
      onChange(digits);
    } else if (digits.length === 3) {
      onChange(digits[0] + ":" + digits.slice(1));
    } else {
      const mm = digits.slice(0, digits.length - 2);
      const ss = digits.slice(-2);
      onChange(mm + ":" + ss);
    }
  };

  const handleHmsChange = (e) => {
    // For h:mm:ss — format as 1, 1:3, 1:34, 1:34:0, 1:34:00
    const digits = e.target.value.replace(/[^0-9]/g, "");
    if (digits.length === 0) { onChange(""); return; }
    if (digits.length <= 1) { onChange(digits); }
    else if (digits.length === 2) { onChange(digits); }
    else if (digits.length === 3) { onChange(digits[0] + ":" + digits.slice(1)); }
    else if (digits.length === 4) { onChange(digits[0] + ":" + digits.slice(1,3) + ":" + digits[3]); }
    else if (digits.length === 5) { onChange(digits[0] + ":" + digits.slice(1,3) + ":" + digits.slice(3)); }
    else { onChange(digits[0] + ":" + digits.slice(1,3) + ":" + digits.slice(3,5)); }
  };

  const handleTimeKey = (e) => {
    if (e.key === "Backspace" && value.endsWith(":")) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  return (
    <input value={value}
      onChange={hmsInput ? handleHmsChange : timeInput ? handleTimeChange : e => onChange(e.target.value)}
      onKeyDown={(timeInput || hmsInput) ? handleTimeKey : undefined}
      placeholder={placeholder}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{
        background: focused ? "#2E2E30" : "#2A2A2C",
        border: `1px solid ${focused ? "#F2620F" : "#3A3A3C"}`,
        boxShadow: focused ? "0 0 0 3px rgba(242,98,15,0.12)" : "none",
        color:"#fff", fontFamily:"'Oswald',sans-serif", fontSize:14, fontWeight:400,
        padding:"9px 12px", outline:"none", width:"100%",
        transition:"all 0.15s", borderRadius:"2px", ...style
      }}
      {...rest}
    />
  );
}
function SBtn({ children, onClick, variant="primary", style={}, disabled=false }) {
  const [hov, setHov] = useState(false);
  const [pressed, setPressed] = useState(false);
  const base = { fontFamily:"'Anton',sans-serif", fontSize:15, letterSpacing:"0.08em", textTransform:"uppercase", padding:"14px 28px", border:"none", cursor:disabled?"not-allowed":"pointer", transition:"all 0.1s", opacity:disabled?0.4:1, borderRadius:"2px", position:"relative", ...style };
  if (variant === "primary") {
    const bg = hov ? "#F2620F" : "linear-gradient(180deg, #FF7030 0%, #F2620F 40%, #D4540C 100%)";
    const shadow = pressed
      ? "0 1px 0 #9A3608, 0 2px 8px rgba(242,98,15,0.15)"
      : hov ? "0 4px 0 #9A3608, 0 6px 24px rgba(242,98,15,0.4), 0 1px 0 rgba(255,255,255,0.15) inset"
             : "0 3px 0 #9A3608, 0 4px 16px rgba(242,98,15,0.2), 0 1px 0 rgba(255,255,255,0.12) inset";
    return (
      <button onClick={onClick} disabled={disabled}
        onMouseEnter={()=>setHov(true)} onMouseLeave={()=>{setHov(false);setPressed(false);}}
        onMouseDown={()=>setPressed(true)} onMouseUp={()=>setPressed(false)}
        style={{...base, background:bg, color:"#fff", boxShadow:shadow, top:pressed?2:hov?-1:0}}>
        {children}
      </button>
    );
  }
  if (variant === "ghost") return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{...base, background:"transparent", color:hov?"#fff":"#888", border:`1px solid ${hov?"#555":"#303032"}`, fontFamily:"'Oswald',sans-serif", fontWeight:600, letterSpacing:"0.15em", transition:"all 0.15s"}}>
      {children}
    </button>
  );
  if (variant === "danger") return (
    <button onClick={onClick} disabled={disabled}
      style={{...base, background:"transparent", color:"#F2620F", border:"1px solid rgba(242,98,15,0.27)"}}>
      {children}
    </button>
  );
}
function STag({ children, color }) { // eslint-disable-line no-unused-vars
  return <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.1em", textTransform:"uppercase", padding:"3px 8px", background:`${color}18`, color, border:`1px solid ${color}44`, borderRadius:"1px" }}>{children}</span>;
}
function STierIcon({ label, color, size=16 }) {
  const levels = { Building:1, Average:2, Advanced:3, Competitive:4, Podium:5 };
  const filled = levels[label] || 0;
  const barW = Math.round(size * 0.22);
  const gap  = Math.round(size * 0.1);
  const bars = [0.33, 0.5, 0.66, 0.83, 1.0];
  return (
    <span style={{ display:"inline-flex", alignItems:"flex-end", gap:gap, height:size, flexShrink:0 }}>
      {bars.map((h, i) => (
        <span key={i} style={{ display:"inline-block", width:barW, height:Math.round(size * h), background: i < filled ? color : "#333", borderRadius:1, flexShrink:0 }} />
      ))}
    </span>
  );
}
// eslint-disable-next-line no-unused-vars
function SGauge({ pct }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", width:28, flexShrink:0 }}>
      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.1em", textTransform:"uppercase", color:"#00b860", marginBottom:3, writingMode:"vertical-rl", transform:"rotate(180deg)", whiteSpace:"nowrap", height:32, lineHeight:1 }}>Elite</div>
      <div style={{ width:6, borderRadius:3, flex:1, minHeight:60, position:"relative", background:"linear-gradient(to top, #F2620F 0%, #dc9600 35%, #78b900 65%, #00b860 100%)" }}>
        <div style={{ position:"absolute", left:"50%", transform:"translateX(-50%)", bottom:`${clamped}%`, width:14, height:2, background:"#fff", boxShadow:"0 0 6px rgba(255,255,255,0.6)", borderRadius:1 }} />
      </div>
      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.1em", textTransform:"uppercase", color:"#555", marginTop:3, writingMode:"vertical-rl", transform:"rotate(180deg)", whiteSpace:"nowrap", height:24, lineHeight:1 }}>Avg</div>
    </div>
  );
}
// ─── NAV ─────────────────────────────────────────────────────────────────────
function Nav({ tab, setTab }) {
  const tabs = [{ id:"sim", label:"Simulator" }, { id:"train", label:"Training Plan" }, { id:"progress", label:"My Progress" }];
  return (
    <div style={{ background:"#1A1A1C", borderBottom:"1px solid #252528", position:"sticky", top:0, zIndex:100 }}>
      <div style={{ maxWidth:980, margin:"0 auto", padding:"0 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ fontFamily:"'Anton',sans-serif", fontSize:24, letterSpacing:"0.04em", padding:"15px 0", display:"flex", alignItems:"baseline" }}>
          <span style={{ color:"#F2620F" }}>SPLIT</span>
          <span style={{ color:"#F2620F", fontSize:"0.72em", verticalAlign:"0.08em", margin:"0 0.01em" }}>:</span>
          <span style={{ color:"#fff" }}>ROX</span>
          <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.2em", marginLeft:14 }}>BETA</span>
        </div>
        <div style={{ display:"flex" }}>
          {tabs.map(t => {
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                background:"transparent", border:"none",
                borderBottom:`2px solid ${active ? "#F2620F" : "transparent"}`,
                color: active ? "#fff" : "#555",
                fontFamily:"'Oswald',sans-serif", fontWeight:600,
                fontSize:12, letterSpacing:"0.2em", textTransform:"uppercase",
                padding:"17px 16px", cursor:"pointer", transition:"all 0.15s"
              }}>{t.label}</button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─── ONBOARDING ───────────────────────────────────────────────────────────────
function OnboardingFlow({ onComplete }) {
  const [step, setStep] = useState(0); // 0=raced before, 1=finish time, 2=category
  const [hasRaced, setHasRaced] = useState(null);
  const [finishTime, setFinishTime] = useState("");
  const [div, setDiv] = useState("open");
  const [gender, setGender] = useState("male");
  const [age, setAge] = useState("16");

  const selBtn = (active, onClick, label, sub) => (
    <button onClick={onClick} style={{ background:active?"#F2620F":"#242426", border:`1px solid ${active?"#F2620F":"#303032"}`, color:active?G.black:G.white, fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:13, letterSpacing:"0.12em", textTransform:"uppercase", padding:"13px 16px", cursor:"pointer", textAlign:"left", transition:"all 0.15s", borderRadius:"2px", flex:1 }}>
      {label}{sub&&<span style={{ display:"block", fontWeight:400, fontSize:12, color:active?G.black:G.muted, marginTop:3 }}>{sub}</span>}
    </button>
  );

  const handleComplete = () => {
    let runPaceHint = null;
    let tierHint = null;
    if (hasRaced && finishTime.trim()) {
      const totalSec = parseFinishTime(finishTime);
      tierHint = getTier(totalSec);
      // Rough run pace estimate from finish time
      const estRun = Math.round((totalSec - ROXZONE) * 0.47 / 8);
      runPaceHint = Math.max(180, Math.min(480, estRun));
    }
    onComplete({ div, gender, age, runPaceHint, tierHint, hasRaced });
  };

  return (
    <div style={{ minHeight:"100vh", background:"#0C0C0C", display:"flex", alignItems:"center", justifyContent:"center", padding:"20px" }}>
      <div style={{ maxWidth:560, width:"100%" }}>
        {/* Logo */}
        <div style={{ fontFamily:"'Anton',sans-serif", fontSize:24, letterSpacing:"0.04em", marginBottom:48, display:"flex", alignItems:"baseline", gap:0 }}>
          <span style={{color:G.accent}}>SPLIT</span><span style={{color:G.accent}}>:</span><span style={{color:G.white}}>ROX</span>
          <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.2em", marginLeft:14 }}>BETA</span>
        </div>

        {step === 0 && (
          <div>
            <div style={{ fontFamily:"'Anton',sans-serif", fontSize:"clamp(48px,8vw,72px)", lineHeight:0.9, textTransform:"uppercase", letterSpacing:"0.04em", color:"#FFFFFF", marginBottom:14 }}>
              Know Your<br/><span style={{color:G.accent}}>Race.</span>
            </div>
            <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:16, color:"#888", marginBottom:40, lineHeight:1.6, maxWidth:400 }}>Your station-by-station split prediction starts here.</p>
            <div style={{ background:"#1A1A1C", border:"1px solid #303032", padding:"18px 20px", marginBottom:2 }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.25em", textTransform:"uppercase", marginBottom:12 }}>Have you raced before?</div>
              <div style={{ display:"flex", gap:2, marginBottom:0 }}>
                {selBtn(hasRaced===true, ()=>setHasRaced(true), "Yes — I've raced", "Enter my finish time")}
                {selBtn(hasRaced===false, ()=>setHasRaced(false), "No — first timer", "Use estimated benchmarks")}
              </div>
            </div>
            {hasRaced !== null && (
              <SBtn onClick={()=>setStep(hasRaced ? 1 : 2)} style={{ width:"100%", padding:"18px", marginTop:2 }}>
                Continue →
              </SBtn>
            )}
          </div>
        )}

        {step === 1 && (
          <div>
            <div style={{ fontFamily:"'Anton',sans-serif", fontSize:"clamp(28px,5vw,46px)", lineHeight:0.95, textTransform:"uppercase", letterSpacing:"0.04em", color:"#FFFFFF", marginBottom:12 }}>
              What was your<br/><span style={{color:G.accent}}>best finish time?</span>
            </div>
            <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:15, color:"#aaa", marginBottom:32, lineHeight:1.6 }}>Use your most recent race. We'll pre-fill benchmarks from your result.</p>
            <div style={{ marginBottom:32 }}>
              <SInput
                value={finishTime}
                onChange={setFinishTime}
                placeholder="1:25:00"
                style={{ fontSize:28, textAlign:"center", letterSpacing:"0.05em" }}
                inputMode="decimal"
              />
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.12em", marginTop:8, textAlign:"center" }}>h:mm:ss · e.g. 1:25:00</div>
            </div>
            {finishTime.trim().length >= 3 && (() => {
              const t = parseFinishTime(finishTime);
              const tier = getTier(t);
              return <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:24, padding:"10px 14px", background:`${tier.color}10`, border:`1px solid ${tier.color}30` }}>
                <STierIcon label={tier.label} color={tier.color} size={20} />
                <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:"#ccc" }}>{fmtLong(t)} finish — we'll calibrate your benchmarks to this result</span>
              </div>;
            })()}
            <div style={{ display:"flex", gap:8 }}>
              <SBtn variant="ghost" onClick={()=>setStep(0)} style={{ padding:"16px 24px" }}>← Back</SBtn>
              <SBtn onClick={()=>setStep(2)} style={{ flex:1, padding:"16px" }}>Continue →</SBtn>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={{ fontFamily:"'Anton',sans-serif", fontSize:"clamp(28px,5vw,46px)", lineHeight:0.95, textTransform:"uppercase", letterSpacing:"0.04em", color:"#FFFFFF", marginBottom:12 }}>
              Your<br/><span style={{color:G.accent}}>Category.</span>
            </div>
            <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:15, color:"#aaa", marginBottom:28, lineHeight:1.6 }}>Sets your division benchmarks, load specs, and age-group comparisons.</p>
            <SLabel style={{ marginBottom:8 }}>Division</SLabel>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:2, marginBottom:20 }}>
              {DIVISIONS.map(d => selBtn(div===d.id, ()=>setDiv(d.id), d.label, d.sub))}
            </div>
            <SLabel style={{ marginBottom:8 }}>Gender</SLabel>
            <div style={{ display:"flex", gap:2, marginBottom:20 }}>
              {GENDERS.map(g => selBtn(gender===g.id, ()=>setGender(g.id), g.label, null))}
            </div>
            <SLabel style={{ marginBottom:8 }}>Age Bracket</SLabel>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:2, marginBottom:32 }}>
              {AGE_BRACKETS.map(a => selBtn(age===a.id, ()=>setAge(a.id), a.label, null))}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <SBtn variant="ghost" onClick={()=>setStep(hasRaced ? 1 : 0)} style={{ padding:"16px 24px" }}>← Back</SBtn>
              <SBtn onClick={handleComplete} style={{ flex:1, padding:"16px" }}>Enter the Race →</SBtn>
            </div>
          </div>
        )}

        <div style={{ marginTop:20, display:"flex", gap:6 }}>
          {[0,1,2].map(i => <div key={i} style={{ height:2, flex:1, background:i<=step?G.accent:G.border, transition:"background 0.3s" }}/>)}
        </div>
      </div>
    </div>
  );
}


// ─── EMAIL CAPTURE ────────────────────────────────────────────────────────────
function EmailCapture({ result, div, gender, age, ranked }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [focused, setFocused] = useState(false);

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    if (!isValid) return;
    setStatus("sending");

    // Build the email payload — ready to wire to Resend/Mailgun
    const weak3 = ranked.slice(0, 3);
    // eslint-disable-next-line no-unused-vars
    const payload = {
      to: email,
      subject: `Your SPLIT:ROX race breakdown — ${fmtLong(result.total)}`,
      data: {
        finishTime: fmtLong(result.total),
        category: `${DIV_LABELS[div]} · ${gender === "male" ? "Male" : "Female"} · ${AGE_BRACKETS.find(a => a.id === age)?.label}`,
        runTime: fmtLong(result.totalRun),
        runPct: result.runPct,
        stationTime: fmtLong(result.totalStation),
        weak: weak3.map(({ s, sec, bm, delta }) => ({
          name: s.name,
          time: fmt(sec),
          avg: fmt(bm.avg),
          over: fmt(Math.abs(delta)),
          tip: s.training.tip
        }))
      }
    };

    // TODO: Replace this with real API call when deployed
    // Example with Resend:
    // await fetch('/api/send-results', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) });

    // For now — simulate a successful send after 1s
    await new Promise(r => setTimeout(r, 1000));

    try {
      // Store email locally so we know they've signed up
      await window.storage?.set("splitrox:email", email);
    } catch(e) {}

    setStatus("sent");
  };

  if (status === "sent") return (
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 18px", background:`${G.good}0f`, border:`1px solid ${G.good}33`, minWidth:280 }}>
      <span style={{ color:G.good, fontSize:18 }}>✓</span>
      <div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:14, color:G.good, letterSpacing:"0.08em" }}>Check your inbox</div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#aaa", marginTop:2 }}>Breakdown sent to {email}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display:"flex", gap:2, minWidth:280, flexDirection:"column" }}>
      <div style={{ display:"flex", gap:2 }}>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === "Enter" && isValid && handleSubmit()}
          placeholder="your@email.com"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            background: "#0C0C0C",
            border: `1px solid ${focused ? "#F2620F" : "#3A3A3C"}`,
            color: G.white,
            fontFamily: "'Oswald',sans-serif",
            fontSize: 15,
            padding: "11px 14px",
            outline: "none",
            borderRadius: "2px",
            transition: "border-color 0.15s"
          }}
        />
        <button
          onClick={handleSubmit}
          disabled={!isValid || status === "sending"}
          style={{
            background: isValid ? G.accentDark : G.border,
            border: "none",
            color: G.white,
            fontFamily: "'Anton',sans-serif",
            fontSize: 13,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            padding: "11px 18px",
            cursor: isValid ? "pointer" : "not-allowed",
            borderRadius: "2px",
            transition: "background 0.15s",
            whiteSpace: "nowrap"
          }}
        >
          {status === "sending" ? "Sending..." : "Send It →"}
        </button>
      </div>
      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.1em" }}>
        No spam. One email. Unsubscribe any time.
      </div>
    </div>
  );
}


// ─── RESULT CARD ─────────────────────────────────────────────────────────────
function ResultCard({ result, div, gender, age }) {
  const tier = getTier(result.total);
  const bms = result.benchmarks;
  const cat = `${DIV_LABELS[div]} · ${gender === "male" ? "Male" : "Female"} · ${AGE_BRACKETS.find(a => a.id === age)?.label || ""}`;

  return (
    <div style={{ background:"#0C0C0C", border:"1px solid #222", overflow:"hidden", fontFamily:"'Space Mono',monospace" }}>
      {/* Orange top bar */}
      <div style={{ height:4, background:"#F2620F" }} />

      <div style={{ padding:"20px 24px" }}>
        {/* Logo + category */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div>
            <div style={{ fontFamily:"'Anton',sans-serif", fontSize:20, letterSpacing:"0.04em", marginBottom:4 }}>
              <span style={{ color:"#F2620F" }}>SPLIT</span><span style={{ color:"#F2620F" }}>:</span><span style={{ color:"#fff" }}>ROX</span>
            </div>
            <div style={{ fontSize:12, color:"#888", letterSpacing:"0.2em", textTransform:"uppercase" }}>{cat}</div>
          </div>
          <div style={{ background:`${tier.color}18`, border:`1px solid ${tier.color}55`, padding:"4px 12px" }}>
            <span style={{ fontSize:12, color:tier.color, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700 }}>{tier.label}</span>
          </div>
        </div>

        {/* Finish time */}
        <div style={{ marginBottom:4 }}>
          <div style={{ fontSize:44, lineHeight:1, color:"#fff", letterSpacing:"0.02em" }}>{fmtLong(result.total)}</div>
          <div style={{ fontSize:12, color:"#888", letterSpacing:"0.2em", textTransform:"uppercase", marginTop:4 }}>Projected Finish</div>
        </div>

        {/* Divider */}
        <div style={{ height:1, background:"#222", margin:"14px 0" }} />

        {/* Run + Station split */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:4 }}>
          <div>
            <div style={{ fontSize:24, color:"#fff", marginBottom:2 }}>{fmtLong(result.totalRun)}</div>
            <div style={{ fontSize:12, color:"#888", letterSpacing:"0.15em", textTransform:"uppercase" }}>Run · {result.runPct}%</div>
          </div>
          <div>
            <div style={{ fontSize:24, color:"#fff", marginBottom:2 }}>{fmtLong(result.totalStation)}</div>
            <div style={{ fontSize:12, color:"#888", letterSpacing:"0.15em", textTransform:"uppercase" }}>Stations · {result.stationPct}%</div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height:1, background:"#222", margin:"14px 0 10px" }} />

        {/* Station breakdown */}
        <div style={{ fontSize:12, color:"#888", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>Station Breakdown</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"2px 24px" }}>
          {STATIONS.map((s, i) => {
            const sec = result.stations[i];
            const bm = bms[i];
            const gap = getGap(sec, bm.elite, bm.avg);
            return (
              <div key={s.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0", borderBottom:"1px solid #181818" }}>
                <div style={{ fontSize:12, color:"#ccc", letterSpacing:"0.08em", textTransform:"uppercase", fontFamily:"'Oswald',sans-serif", fontWeight:500 }}>{s.name}</div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <STierIcon label={gap.label} color={gap.color} size={12} />
                  <div style={{ fontSize:12, color:gap.color, minWidth:36, textAlign:"right" }}>{fmt(sec)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ height:1, background:"#222", margin:"14px 0 10px" }} />
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#444", letterSpacing:"0.1em" }}>
          <span>splitrox.com</span>
          <span>Race prediction · Not official HYROX data</span>
        </div>
      </div>
    </div>
  );
}

// ─── SHARE CARD ───────────────────────────────────────────────────────────────
function ShareCard({ result, div, gender, age }) {
  const [status, setStatus] = useState("idle"); // idle | drawing | done | error

  const generate = () => {
    setStatus("drawing");
    try {
      const SIZE = 1080;
      const canvas = document.createElement("canvas");
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext("2d");

      // ── Palette ──
      const BG      = "#0C0C0C";
      const ACCENT  = "#E85A26";
      const WHITE   = "#FFFFFF";
      const MUTED   = "#888888";
      const BORDER  = "#222222";
      const GOOD    = "#00e87a";
      const WARN    = "#ffaa00";
      const DANGER  = "#ff3c00";


      // ── Background ──
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, SIZE, SIZE);

      // Orange top bar
      ctx.fillStyle = ACCENT;
      ctx.fillRect(0, 0, SIZE, 8);

      // Subtle grid lines
      ctx.strokeStyle = "#181818";
      ctx.lineWidth = 1;
      for (let x = 0; x <= SIZE; x += 54) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, SIZE); ctx.stroke();
      }

      // ── Logo ──
      ctx.font = "bold 52px Arial, sans-serif";
      ctx.fillStyle = ACCENT;
      ctx.letterSpacing = "4px";
      ctx.fillText("SPLIT:ROX", 64, 100);

      // Category label
      const cat = `${DIV_LABELS[div]} · ${gender === "male" ? "Male" : "Female"} · ${AGE_BRACKETS.find(a => a.id === age)?.label || ""}`;
      ctx.font = "16px monospace";
      ctx.fillStyle = MUTED;
      ctx.fillText(cat.toUpperCase(), 66, 132);

      // ── Finish time ──
      ctx.font = "bold 128px Arial, sans-serif";
      ctx.fillStyle = WHITE;
      ctx.fillText(fmtLong(result.total), 64, 280);

      ctx.font = "14px monospace";
      ctx.fillStyle = MUTED;
      ctx.fillText("PROJECTED FINISH", 66, 308);

      // Tier badge
      const tier = getTier(result.total);
      const badgeX = SIZE - 240;
      ctx.fillStyle = tier.color + "22";
      ctx.strokeStyle = tier.color + "66";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.rect(badgeX, 228, 172, 44);
      ctx.fill(); ctx.stroke();
      ctx.font = "bold 18px monospace";
      ctx.fillStyle = tier.color;
      ctx.textAlign = "center";
      ctx.fillText(tier.label.toUpperCase(), badgeX + 86, 256);
      ctx.textAlign = "left";

      // ── Divider ──
      ctx.fillStyle = BORDER;
      ctx.fillRect(64, 330, SIZE - 128, 1);

      // ── Run + Station split ──
      ctx.font = "bold 44px Arial, sans-serif";
      ctx.fillStyle = WHITE;
      ctx.fillText(fmtLong(result.totalRun), 64, 400);
      ctx.fillText(fmtLong(result.totalStation), 340, 400);

      ctx.font = "13px monospace";
      ctx.fillStyle = MUTED;
      ctx.fillText(`RUN  ${result.runPct}%`, 64, 424);
      ctx.fillText(`STATIONS  ${result.stationPct}%`, 340, 424);

      // ── Divider ──
      ctx.fillStyle = BORDER;
      ctx.fillRect(64, 448, SIZE - 128, 1);

      // ── Station breakdown ──
      ctx.font = "13px monospace";
      ctx.fillStyle = MUTED;
      ctx.fillText("STATION BREAKDOWN", 64, 482);

      const bms = result.benchmarks;
      const cols = 2;
      const colW = (SIZE - 128) / cols;
      const rowH = 62;
      const startY = 502;

      STATIONS.forEach((s, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const x = 64 + col * colW;
        const y = startY + row * rowH;
        const sec = result.stations[i];
        const bm = bms[i];
        const range = bm.avg - bm.elite;
        const pos = sec - bm.elite;
        const pct = Math.min(100, Math.max(0, range > 0 ? (pos / range) * 100 : 50));
        const color = pct < 25 ? GOOD : pct < 50 ? "#aadd00" : pct < 70 ? "#00aaff" : pct < 88 ? WARN : DANGER;

        // Station name
        ctx.font = "bold 14px Arial, sans-serif";
        ctx.fillStyle = WHITE;
        ctx.fillText(s.name, x, y + 16);

        // Time
        ctx.font = "bold 22px monospace";
        ctx.fillStyle = color;
        ctx.fillText(fmt(sec), x, y + 42);

        // Bar
        const barW = colW - 130;
        ctx.fillStyle = BORDER;
        ctx.fillRect(x + 110, y + 30, barW, 3);
        const fillW = Math.round((1 - pct / 100) * barW);
        ctx.fillStyle = color;
        ctx.fillRect(x + 110, y + 30, fillW, 3);
      });

      // ── Divider ──
      const footerY = startY + Math.ceil(STATIONS.length / cols) * rowH + 12;
      ctx.fillStyle = BORDER;
      ctx.fillRect(64, footerY, SIZE - 128, 1);

      // ── Footer ──
      ctx.font = "13px monospace";
      ctx.fillStyle = MUTED;
      ctx.fillText("splitrox.com", 64, footerY + 34);

      ctx.font = "12px monospace";
      ctx.fillStyle = "#444";
      ctx.textAlign = "right";
      ctx.fillText("Race prediction · Not official HYROX data", SIZE - 64, footerY + 34);
      ctx.textAlign = "left";

      // ── Download ──
      canvas.toBlob(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `splitrox-${fmtLong(result.total).replace(/:/g,"-")}.png`;
        a.click();
        URL.revokeObjectURL(url);
        setStatus("done");
        setTimeout(() => setStatus("idle"), 3000);
      }, "image/png");

    } catch(e) {
      console.error(e);
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <button onClick={generate} disabled={status === "drawing"} style={{
      padding: "14px 20px",
      background: "transparent",
      border: `1px solid ${status === "done" ? G.good : G.border}`,
      color: status === "done" ? G.good : status === "error" ? G.danger : G.white,
      fontFamily: "'Oswald',sans-serif",
      fontWeight: 700,
      fontSize: 14,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      cursor: status === "drawing" ? "wait" : "pointer",
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    }}>
      {status === "drawing" ? "⏳ Generating..." : status === "done" ? "✓ Downloaded" : status === "error" ? "✗ Failed" : "⬇ Download Card"}
    </button>
  );
}

// ─── SIMULATOR ───────────────────────────────────────────────────────────────
function SimulatorTab({ onSave, initProfile, onNavigate }) {
  const [div, setDiv] = useState(initProfile?.div || "open");
  const [gender, setGender] = useState(initProfile?.gender || "male");
  const [age, setAge] = useState(initProfile?.age || "16");
  const [runPaceText, setRunPaceText] = useState(() => {
    if (initProfile?.runPaceHint) {
      const m = Math.floor(initProfile.runPaceHint / 60);
      const s = String(initProfile.runPaceHint % 60).padStart(2,"0");
      return `${m}:${s}`;
    }
    return "";
  });
  const [goalTime, setGoalTime] = useState("");
  const [trainingDays, setTrainingDays] = useState(4);
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);

  const isMobile = useIsMobile();
  const profile = { div, gender, age };
  const runPace = runPaceText.trim() ? (parseMMSS(runPaceText) || parseFinishTime(runPaceText) || null) : null;
  const goalSec = goalTime.trim() ? parseFinishTime(goalTime) || null : null;
  const setInput = (id, v) => setInputs(p => ({...p,[id]:v}));
  // Sprint 2: Pre-fill context strip
  const filledCount = Object.values(inputs).filter(v => v && v.toString().trim()).length;
  const benchmarks = getBenchmarks(div, gender, age);
  const [runImproveSec, setRunImproveSec] = useState(0);

  const [errors, setErrors] = useState({});
  const calculate = () => {
    // Validate inputs
    const errs = {};
    if (runPaceText.trim()) {
      const pace = parseMMSS(runPaceText);
      if (!pace || pace < 180 || pace > 600) errs.runPace = "Pace should be between 3:00 and 10:00 /km";
    }
    STATIONS.forEach(s => {
      const val = inputs[s.id];
      if (!val) return;
      if (s.inputUnit === "rpm") {
        const rpm = parseFloat(val);
        if (isNaN(rpm) || rpm < 5 || rpm > 40) errs[s.id] = "RPM should be 5–40";
      } else {
        const t = parseMMSS(val);
        if (t && (t < 30 || t > 1800)) errs[s.id] = "Time seems out of range";
      }
    });
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const r = calcRace(inputs, runPace, profile);
    setResult(r); setSaved(false); setRunImproveSec(0);
    setTimeout(()=>document.getElementById("res-anchor")?.scrollIntoView({behavior:"smooth"}), 80);
  };

  const handleSave = async () => {
    if (!result) return;
    const rr = calcReadiness(result, inputs);
    const entry = { id:Date.now(), date:new Date().toISOString(), div, gender, age, inputs:{...inputs}, runPace, goal:goalSec, trainingDays, result:{...result}, readiness:rr.score };
    const existing = await loadEntries();
    await saveEntries([...existing, entry]);
    setSaved(true); onSave(entry);
  };

  const selBtn = (active, onClick, label, sub) => (
    <button onClick={onClick} style={{ background:active?"#F2620F":"#242426", border:`1px solid ${active?"#F2620F":"#303032"}`, color:active?G.black:G.white, fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:13, letterSpacing:"0.12em", textTransform:"uppercase", padding:"11px 12px", cursor:"pointer", textAlign:"left", transition:"all 0.15s", borderRadius:"2px" }}>
      {label}{sub && <span style={{ display:"block", fontWeight:400, fontSize:12, color:active?G.black:G.muted, marginTop:2 }}>{sub}</span>}
    </button>
  );

  return (
    <div style={{ background:"#0C0C0C", minHeight:"100vh" }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"32px 20px 80px" }}>

      {/* Division */}
      <SLabel>Division</SLabel>
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)", gap:2, marginBottom:16 }}>
        {DIVISIONS.map(d => selBtn(div===d.id, ()=>setDiv(d.id), d.label, d.sub))}
      </div>

      {/* Gender + Age bracket */}
      <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:16, marginBottom:28, alignItems:"start" }}>
        <div>
          <SLabel>Gender</SLabel>
          <div style={{ display:"flex", gap:2 }}>
            {GENDERS.map(g => selBtn(gender===g.id, ()=>setGender(g.id), g.label, null))}
          </div>
        </div>
        <div>
          <SLabel>Age Bracket</SLabel>
          <div style={{ display:"flex", gap:2, flexWrap:"wrap" }}>
            {AGE_BRACKETS.map(a => selBtn(age===a.id, ()=>setAge(a.id), a.label, null))}
          </div>
        </div>
      </div>

      {/* Context strip */}
      <div style={{ background:"#1A1A1C", border:"1px solid #303032", padding:"10px 16px", marginBottom:28, display:"flex", alignItems:"center", gap:12 }}>
        <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.15em", textTransform:"uppercase" }}>Your category:</span>
        <span style={{ fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:14, color:"#FFFFFF", letterSpacing:"0.08em" }}>
          {DIV_LABELS[div]} · {gender === "male" ? "Male" : "Female"} · {AGE_BRACKETS.find(a=>a.id===age)?.label}
        </span>
        {div === "relay" && <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.warn, letterSpacing:"0.1em" }}>TEAM OF 4 — enter your own benchmarks</span>}
        {div === "doubles" && <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.warn, letterSpacing:"0.1em" }}>TEAM OF 2 — enter your own benchmarks</span>}
      </div>

      {/* Run Pace */}
      {(() => {
        const defaultKm = Math.round(RUN_DEFAULTS[div][gender] * (AGE_MULT[age] || 1.0));
        const defaultKmMin = String(Math.floor(defaultKm / 60));
        const defaultKmSec = String(defaultKm % 60).padStart(2, "0");
        const runIsUser = runPaceText.trim().length > 0;
        return (
          <div style={{ borderBottom:"1px solid #161616", padding:"22px 0" }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"#444", textTransform:"uppercase", fontWeight:500, marginBottom:8, display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ display:"inline-block", width:5, height:5, background:"#F2620F", borderRadius:"50%" }}/>
              1km Run Pace
            </div>
            <SInput
              value={runPaceText}
              onChange={v => { setRunPaceText(v); if(errors.runPace) setErrors(p=>({...p,runPace:null})); }}
              placeholder={`${defaultKmMin}:${defaultKmSec}`}
              style={{ fontSize:34, textAlign:"left", letterSpacing:"0.04em", fontFamily:"'Space Mono',monospace", padding:"0", background:"transparent", border:"none", width:"100%", color:runIsUser?"#FFFFFF":"#282828", caretColor:"#F2620F" }}
              timeInput={true}
              inputMode="numeric"
            />
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em", marginTop:6 }}>
              per km · fatigue applied across 8 runs{!runIsUser && <span style={{ color:"#2A2A2A" }}> · default: {defaultKmMin}:{defaultKmSec}/km</span>}
            </div>
            {errors.runPace && <div style={{ marginTop:6, fontSize:12, color:G.danger, fontFamily:"'Oswald',sans-serif" }}>{errors.runPace}</div>}
          </div>
        );
      })()}

      {/* Target Race Time */}
      {(() => {
        const defaultKm2 = Math.round(RUN_DEFAULTS[div][gender] * (AGE_MULT[age] || 1.0));
        const avgStationTotal = getBenchmarks(div, gender, age).reduce((a, b) => a + b.avg, 0);
        const avgRunTotal = FATIGUE.map(f => Math.round(defaultKm2 * f)).reduce((a, b) => a + b, 0);
        const defaultGoalSec = Math.round((avgStationTotal + avgRunTotal + ROXZONE) * 0.95);
        const defGoalH = Math.floor(defaultGoalSec / 3600);
        const defGoalM = Math.floor((defaultGoalSec % 3600) / 60);
        const goalIsUser = goalTime.trim().length > 0;
        return (
          <div style={{ borderBottom:"1px solid #161616", padding:"22px 0" }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"#444", textTransform:"uppercase", fontWeight:500, marginBottom:8 }}>
              Target Race Time
            </div>
            <SInput
              value={goalTime}
              onChange={setGoalTime}
              placeholder={`${defGoalH}:${String(defGoalM).padStart(2,"0")}:00`}
              style={{ fontSize:34, textAlign:"left", letterSpacing:"0.04em", fontFamily:"'Space Mono',monospace", padding:"0", background:"transparent", border:"none", width:"100%", color:goalIsUser?"#FFFFFF":"#282828", caretColor:"#F2620F" }}
              hmsInput={true}
              inputMode="numeric"
            />
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em", marginTop:6 }}>
              h:mm:ss · optional goal
            </div>
          </div>
        );
      })()}

      {/* Training Days */}
      <div style={{ borderBottom:"1px solid #161616", padding:"22px 0" }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"#444", textTransform:"uppercase", fontWeight:500, marginBottom:10 }}>
          Training Days Per Week
        </div>
        <div style={{ display:"flex", gap:2 }}>
          {[3,4,5].map(d => (
            <div key={d} onClick={()=>setTrainingDays(d)} style={{
              flex:1, padding:"11px 8px", textAlign:"center",
              background:trainingDays===d?"#F2620F":"#161616",
              border:`1px solid ${trainingDays===d?"#F2620F":"#242424"}`,
              color:trainingDays===d?"#fff":"#2E2E2E",
              fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:13,
              letterSpacing:"0.12em", textTransform:"uppercase", cursor:"pointer"
            }}>{d} days</div>
          ))}
        </div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#2A2A2A", letterSpacing:"0.08em", marginTop:6 }}>Used to generate your 4-week training plan</div>
      </div>

      {/* Station Benchmarks */}
      <div style={{ background:"#F2620F", padding:"11px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:0 }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:"#000" }}>Station Benchmarks</div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, fontWeight:600, letterSpacing:"0.12em", color:"#7A2A00" }}>
          {DIV_LABELS[div]} · {gender==="male"?"Male":"Female"} · {AGE_BRACKETS.find(a=>a.id===age)?.label}
          {filledCount > 0 && <span style={{ color:"#000", marginLeft:8 }}>· {filledCount}/8 personalised</span>}
        </div>
      </div>
      <div style={{ marginBottom:0 }}>
        {STATIONS.map((s, i) => {
          const val = inputs[s.id]||"";
          const isUserEntered = val && val.toString().trim().length > 0;
          const avgSec = benchmarks[i].avg;
          const avgDisplay = s.inputUnit === "rpm"
            ? String(Math.round(100 / avgSec * 60))
            : fmt(avgSec);
          return (
            <div key={s.id} style={{ display:"grid", gridTemplateColumns:"1fr auto", alignItems:"center", gap:10, padding:"13px 0", borderBottom:"1px solid #141414" }}>
              <div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:600, letterSpacing:"0.14em", textTransform:"uppercase", color:"#FFFFFF" }}>{s.name}</div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", letterSpacing:"0.1em", marginTop:3, opacity:0.45 }}>{s.inputUnit} · {getStationSpec(s, div, gender)}</div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:"#F1EFE8", opacity:0.45 }}>{avgDisplay}</div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.18em", color:"#F1EFE8", textTransform:"uppercase", marginTop:1, opacity:0.35 }}>avg</div>
                </div>
                <SInput
                  value={val}
                  onChange={v=>{setInput(s.id,v); if(errors[s.id]) setErrors(p=>({...p,[s.id]:null}));}}
                  placeholder={avgDisplay}
                  timeInput={s.inputUnit !== "rpm"}
                  style={{ width:76, textAlign:"center", fontSize:15, borderColor:errors[s.id]?G.danger:isUserEntered?"#F2620F":"#242424" }}
                />
              </div>
              {errors[s.id] && <div style={{ gridColumn:"1/-1", fontSize:12, color:G.danger, fontFamily:"'Oswald',sans-serif", paddingTop:2 }}>{errors[s.id]}</div>}
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div onClick={calculate} style={{ background:"#F2620F", padding:"22px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}>
        <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, fontWeight:700, letterSpacing:"0.22em", textTransform:"uppercase", color:"#FFFFFF" }}>Calculate My Race Time</span>
        <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:20, color:"#FFFFFF" }}>→</span>
      </div>
      <p style={{ fontSize:12, color:"#F1EFE8", marginTop:7, fontFamily:"'Oswald',sans-serif", letterSpacing:"0.06em", opacity:0.25 }}>Blank fields use division averages. Your real numbers give far better accuracy.</p>
      {/* RESULTS */}
      {result && (
        <div id="res-anchor" style={{ background:"#0C0C0C", margin:"60px -20px 0", padding:"40px 20px 60px" }}>
          {/* Top stats */}
          {(() => {
            const impact = runImproveSec > 0 ? calcRunImpact(result.km, runImproveSec) : null;
            const projTotal = impact ? result.total - impact.saved : result.total;
            return (
              <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1.2fr 1fr 1fr", gap:2, marginBottom:2 }}>
                <div style={{ background:"#F2620F", padding:"22px 20px", boxShadow:"0 3px 0 #9A3608, 0 1px 0 rgba(255,255,255,0.15) inset" }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.25em", textTransform:"uppercase", color:"rgba(255,255,255,0.65)", marginBottom:6 }}>Projected Finish</div>
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:38, lineHeight:1, color:"#fff", letterSpacing:"0.02em" }}>{fmtLong(projTotal)}</div>
                  {impact && <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:"rgba(255,255,255,0.7)", marginTop:4 }}>−{fmt(impact.saved)} with run improvement</div>}
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"rgba(255,255,255,0.55)", marginTop:impact?2:8, letterSpacing:"0.1em", textTransform:"uppercase" }}>{DIV_LABELS[div]} Division</div>
                </div>
                <div style={{ background:"#181818", border:"1px solid #222", borderTop:"1px solid #2A2A2A", padding:"22px" }}>
                  <SLabel>Total Run</SLabel>
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:26, color:G.white }}>{fmtLong(result.totalRun)}</div>
                  <div style={{ fontSize:12, color:"#888888", marginTop:4 }}>{result.runPct}% of finish time · 8 × 1km</div>
                </div>
                <div style={{ background:"#181818", border:"1px solid #222", borderTop:"1px solid #2A2A2A", padding:"22px" }}>
                  <SLabel>Total Stations</SLabel>
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:26, color:G.white }}>{fmtLong(result.totalStation)}</div>
                  <div style={{ fontSize:12, color:"#888888", marginTop:4 }}>{result.stationPct}% of finish time · 8 stations</div>
                </div>
              </div>
            );
          })()}

          {/* Save prompt — high intent moment */}
          {!saved ? (
            <div style={{ background:"#111", border:"1px solid #303032", borderTop:`2px solid ${G.accent}`, padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, marginBottom:2, flexWrap:isMobile?"wrap":"nowrap" }}>
              <div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:3 }}>Save to My Progress</div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:G.textBody, lineHeight:1.4 }}>Track improvement across sessions. Your baseline starts here.</div>
              </div>
              <SBtn onClick={handleSave} style={{ whiteSpace:"nowrap", flexShrink:0, padding:"12px 22px" }}>Save →</SBtn>
            </div>
          ) : (
            <div style={{ background:`${G.good}0a`, border:`1px solid ${G.good}33`, padding:"14px 20px", marginBottom:2 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <span style={{ color:G.good, fontFamily:"'Oswald',sans-serif", fontSize:14 }}>✓</span>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:G.good, fontWeight:700, letterSpacing:"0.08em" }}>Saved to My Progress</div>
              </div>
              <div style={{ height:1, background:`${G.good}22`, marginBottom:10 }} />
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:14, flexWrap:"wrap" }}>
                <div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:3 }}>Already raced?</div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, color:G.textMuted, lineHeight:1.4 }}>Compare your prediction to your actual splits.</div>
                </div>
                <button onClick={()=>onNavigate("debrief")} style={{ background:"transparent", border:`1px solid ${G.good}55`, color:G.good, fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:12, letterSpacing:"0.12em", textTransform:"uppercase", padding:"8px 16px", cursor:"pointer", borderRadius:"2px", whiteSpace:"nowrap", flexShrink:0 }}>
                  → Debrief This Race
                </button>
              </div>
            </div>
          )}

          {/* Race Readiness Score */}
          {(() => {
            const rr = calcReadiness(result, inputs);
            const tier = getTier(result.total);
            const circumference = 2 * Math.PI * 36;
            const dashOffset = circumference * (1 - rr.score / 100);
            return (
              <div style={{ background:"#0a0e14", border:`1px solid ${rr.color}44`, marginBottom:2 }}>
                <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"auto 1fr auto", alignItems:"center", gap:0 }}>
                  {/* Score ring */}
                  <div style={{ padding:"22px 28px", display:"flex", alignItems:"center", gap:20, borderRight:isMobile?"none":"1px solid #303032" }}>
                    <svg width="88" height="88" viewBox="0 0 88 88">
                      <circle cx="44" cy="44" r="36" fill="none" stroke={G.border} strokeWidth="6"/>
                      <circle cx="44" cy="44" r="36" fill="none" stroke={rr.color} strokeWidth="6"
                        strokeDasharray={circumference} strokeDashoffset={dashOffset}
                        strokeLinecap="round"
                        transform="rotate(-90 44 44)"
                        style={{ transition:"stroke-dashoffset 0.8s ease" }}
                      />
                      <text x="44" y="49" textAnchor="middle" fill={rr.color}
                        style={{ fontFamily:"'Oswald',sans-serif", fontSize:"18px", fontWeight:"bold" }}>
                        {rr.score}
                      </text>
                    </svg>
                    <div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.2em", marginBottom:5 }}>RACE READINESS</div>
                      <div style={{ fontFamily:"'Anton',sans-serif", fontSize:22, letterSpacing:"0.04em", color:rr.color }}>{rr.label}</div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888", marginTop:3 }}>
                        {DIV_LABELS[div]} · {gender==="male"?"Male":"Female"} · {AGE_BRACKETS.find(a=>a.id===age)?.label}
                      </div>
                    </div>
                  </div>

                  {/* Description + breakdown */}
                  <div style={{ padding:"22px 24px" }}>
                    <div style={{ fontSize:14, color:"#ccc", fontFamily:"'Oswald',sans-serif", lineHeight:1.6, marginBottom:14 }}>
                      {rr.description}
                    </div>
                    <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                      {[
                        { label:"Station Performance", val:rr.stationComponent, max:75 },
                        { label:"Data Quality", val:rr.dataComponent, max:25 },
                      ].map(({ label, val, max }) => (
                        <div key={label}>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.15em", marginBottom:4 }}>{label.toUpperCase()}</div>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <div style={{ width:80, height:3, background:G.border }}>
                              <div style={{ width:`${(val/max)*100}%`, height:"100%", background:rr.color, transition:"width 0.5s" }}/>
                            </div>
                            <span style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:rr.color }}>{val}/{max}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tier badge */}
                  <div style={{ padding:"22px 24px", borderLeft:isMobile?"none":"1px solid #303032", display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
                    <STierIcon label={tier.label} color={tier.color} size={20} />
                    <div style={{ fontSize:12, color:"#888888", textAlign:"center", fontFamily:"'Oswald',sans-serif", maxWidth:100, lineHeight:1.4 }}>
                      { { Building:"Every session is building your base. Focus on consistency.", Average:"Solid effort. Targeted station work will push you into Advanced.", Advanced:"Strong result. Marginal gains across stations will take you to Competitive.", Competitive:"You're racing for age-group placements. Sub-1% improvements matter.", Podium:"Top-tier performance. You're competing for podium spots and WC qualification." }[tier.label] }
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Goal gap */}
          {goalSec && (() => {
            const under = result.total <= goalSec;
            const diff = Math.abs(result.total - goalSec);
            return (
              <div style={{ background:under?"#00e87a0a":"#ff3c000a", border:`1px solid ${under?"#00e87a33":"#ff3c0033"}`, padding:"14px 22px", marginBottom:2 }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:under?G.good:G.danger, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>
                  {under ? `✓ On Track — ${fmt(diff)} under goal` : `⚠ ${fmt(diff)} over goal`}
                </div>
                <div style={{ fontSize:13, color:"#ddd" }}>
                  {under ? `You're projected ${fmt(diff)} under your target of ${fmtLong(goalSec)}.` : `To hit ${fmtLong(goalSec)}, cut ${fmt(diff)} total. The station breakdown below shows you where to find it.`}
                </div>
              </div>
            );
          })()}

          {/* Station breakdown */}
          {(() => {
            const ranked = STATIONS.map((s, i) => {
              const sec = result.stations[i];
              const bm = result.benchmarks[i];
              const gap = getGap(sec, bm.elite, bm.avg);
              const delta = sec - bm.avg;
              const range = bm.avg - bm.elite;
              const gaugePct = range > 0 ? Math.min(100, Math.max(0, ((bm.avg - sec) / range) * 100)) : 50;
              return { s, sec, bm, gap, delta, gaugePct };
            }).sort((a, b) => b.delta - a.delta);

            const insightMap = {
              Podium:      "Elite level. Maintain this — one of your biggest time advantages.",
              Competitive: "Above average. Consistent work here will push you to Podium.",
              Advanced:    "Slightly above average. Focused training here has good leverage.",
              Average:     "Right at average. This station has real improvement potential.",
              Building:    "Below average. Biggest time opportunity. Target this first.",
            };

            const weakStations  = ranked.filter(r => r.delta > 0);
            const midStations   = ranked.filter(r => r.delta <= 0 && r.gap.label !== "Podium" && r.gap.label !== "Competitive");
            const strongStations = ranked.filter(r => r.gap.label === "Podium" || r.gap.label === "Competitive");

            const top    = weakStations[0] || null;
            const second = weakStations[1] || null;
            const rest   = [...weakStations.slice(2), ...midStations];

            return (
              <div style={{ marginBottom:20 }}>
                <SLabel style={{ margin:"26px 0 10px" }}>Station Breakdown</SLabel>
                <div style={{ display:"grid", gap:2 }}>

                  {/* #1 — Dominant card */}
                  {top && (
                    <div style={{ background:"#1A1014", border:`1px solid rgba(242,98,15,0.5)`, borderLeft:"4px solid #F2620F", padding:"20px 20px" }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.accent, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:8 }}>① Biggest opportunity</div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                        <div>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:16, letterSpacing:"0.15em", textTransform:"uppercase", color:G.white }}>{top.s.name}</div>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", marginTop:3 }}>{getStationSpec(top.s, div, gender)}</div>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontFamily:"'Space Mono',monospace", fontSize:28, color:G.accent, lineHeight:1 }}>{fmt(top.sec)}</div>
                          <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:G.accent, marginTop:4 }}>+{fmt(top.delta)} over avg</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:20, marginBottom:12 }}>
                        {[["Top 10%", fmt(top.bm.elite), "#00b860"], ["Div. Avg", fmt(top.bm.avg), "#888"], ["You", fmt(top.sec), G.accent]].map(([lbl,val,col]) => (
                          <div key={lbl}>
                            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", textTransform:"uppercase", letterSpacing:"0.15em", marginBottom:2 }}>{lbl}</div>
                            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:13, color:col }}>{val}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:G.textBody, lineHeight:1.5, marginBottom:12 }}>{insightMap[top.gap.label]}</div>
                      <button onClick={()=>onNavigate("train", top.s.id)} style={{ background:"transparent", border:`1px solid rgba(242,98,15,0.5)`, color:G.accent, fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:12, letterSpacing:"0.12em", textTransform:"uppercase", padding:"7px 16px", cursor:"pointer", borderRadius:"2px" }}>
                        → Train This Station
                      </button>
                    </div>
                  )}

                  {/* #2 — Secondary card */}
                  {second && (
                    <div style={{ background:"#161214", border:`1px solid rgba(242,98,15,0.3)`, borderLeft:"3px solid #F2620F", padding:"16px 20px" }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>② Also needs work</div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                        <div>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:14, letterSpacing:"0.15em", textTransform:"uppercase", color:G.white }}>{second.s.name}</div>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", marginTop:2 }}>{getStationSpec(second.s, div, gender)}</div>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontFamily:"'Space Mono',monospace", fontSize:22, color:G.accent, lineHeight:1 }}>{fmt(second.sec)}</div>
                          <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:G.accent, marginTop:3 }}>+{fmt(second.delta)} over avg</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:16, marginBottom:10 }}>
                        {[["Top 10%", fmt(second.bm.elite), "#00b860"], ["Div. Avg", fmt(second.bm.avg), "#888"], ["You", fmt(second.sec), G.accent]].map(([lbl,val,col]) => (
                          <div key={lbl}>
                            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", textTransform:"uppercase", letterSpacing:"0.15em", marginBottom:2 }}>{lbl}</div>
                            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:col }}>{val}</div>
                          </div>
                        ))}
                      </div>
                      <button onClick={()=>onNavigate("train", second.s.id)} style={{ background:"transparent", border:`1px solid rgba(242,98,15,0.4)`, color:G.accent, fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:12, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 12px", cursor:"pointer", borderRadius:"2px" }}>
                        → Train This Station
                      </button>
                    </div>
                  )}

                  {/* Middle stations — compact rows */}
                  {rest.map(({ s, sec, bm, gap, delta }) => (
                    <div key={s.id} style={{ background:"#181818", border:"1px solid #222", borderTop:"1px solid #2A2A2A", padding:"13px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:13, letterSpacing:"0.12em", textTransform:"uppercase", color:G.white }}>{s.name}</div>
                        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", marginTop:2 }}>
                          <STierIcon label={gap.label} color={gap.color} />
                          <span style={{ marginLeft:8 }}>{delta > 0 ? `+${fmt(delta)} over avg` : `${fmt(Math.abs(delta))} under avg`}</span>
                        </div>
                      </div>
                      <div style={{ fontFamily:"'Space Mono',monospace", fontSize:16, color:gap.color }}>{fmt(sec)}</div>
                    </div>
                  ))}

                  {/* Strengths — dimmed */}
                  {strongStations.length > 0 && (
                    <>
                      <div style={{ height:1, background:"#222", margin:"4px 0" }} />
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.12em", textTransform:"uppercase", padding:"2px 0 4px" }}>Strengths — don't let these slip</div>
                      {strongStations.map(({ s, sec, bm, gap, delta }) => (
                        <div key={s.id} style={{ background:"#181818", border:"1px solid #222", padding:"13px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", opacity:0.65 }}>
                          <div>
                            <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:13, letterSpacing:"0.12em", textTransform:"uppercase", color:G.white }}>{s.name}</div>
                            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", marginTop:2 }}>
                              <STierIcon label={gap.label} color={gap.color} />
                              <span style={{ marginLeft:8 }}>{fmt(Math.abs(delta))} under avg</span>
                            </div>
                          </div>
                          <div style={{ fontFamily:"'Space Mono',monospace", fontSize:16, color:gap.color }}>{fmt(sec)}</div>
                        </div>
                      ))}
                    </>
                  )}

                </div>
              </div>
            );
          })()}

          {/* ── RUN INTELLIGENCE ── */}
          <div style={{ marginBottom:2 }}>

            {/* Run opportunity card */}
            {(() => {
              const saved10 = calcRunImpact(result.km, 10).saved;
              const saved20 = calcRunImpact(result.km, 20).saved;
              const saved30 = calcRunImpact(result.km, 30).saved;
              const runShare = result.runPct;
              return (
                <div style={{ background:"#111418", border:"1px solid #222", borderLeft:"3px solid #FF8040", padding:"18px 22px", marginBottom:2 }}>
                  <SLabel style={{ marginBottom:8 }}>Run Intelligence</SLabel>
                  <p style={{ fontSize:15, color:"#ccc", lineHeight:1.65, marginBottom:14, fontFamily:"'Oswald',sans-serif", letterSpacing:"0.03em" }}>
                    Your run accounts for <span style={{ color:"#FFFFFF", fontFamily:"'Oswald',sans-serif", fontSize:14 }}>{runShare}%</span> of your total finish time. Running is your single biggest lever — improvements compound across all 8 runs.
                  </p>
                  <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)", gap:2 }}>
                    {[{imp:10,saved:saved10},{imp:20,saved:saved20},{imp:30,saved:saved30}].map(({imp,saved})=>(
                      <div key={imp} style={{ background:"#0C0C0C", border:"1px solid #222", padding:"12px 14px" }}>
                        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.15em", marginBottom:5 }}>−{imp}s/km</div>
                        <div style={{ fontFamily:"'Space Mono',monospace", fontSize:20, color:G.good }}>−{fmt(saved)}</div>
                        <div style={{ fontSize:12, color:"#888888", marginTop:3, fontFamily:"'Oswald',sans-serif" }}>off finish time</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Pace improvement simulator */}
            <div style={{ background:"#181818", border:"1px solid #222", borderTop:"1px solid #2A2A2A", padding:"18px 22px", marginBottom:2 }}>
              <SLabel style={{ marginBottom:6 }}>Pace Improvement Simulator</SLabel>
              <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:10 }}>
                <span style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:"#888888", whiteSpace:"nowrap" }}>Current {fmt(result.km)}/km</span>
                <input type="range" min={0} max={60} step={5} value={runImproveSec}
                  onChange={e=>setRunImproveSec(Number(e.target.value))}
                  style={{ flex:1, accentColor:G.accent, cursor:"pointer" }} />
                <span style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:"#FFFFFF", whiteSpace:"nowrap", minWidth:70, textAlign:"right" }}>
                  {runImproveSec > 0 ? `${fmt(Math.max(180,result.km-runImproveSec))}/km` : <span style={{fontFamily:"'Oswald',sans-serif"}}>drag →</span>}
                </span>
              </div>
              {runImproveSec > 0 && (() => {
                const {saved} = calcRunImpact(result.km, runImproveSec);
                return (
                  <div style={{ display:"flex", gap:16, alignItems:"center", padding:"10px 14px", background:"#0C0C0C", border:`1px solid ${G.good}33` }}>
                    <span style={{ fontFamily:"'Space Mono',monospace", fontSize:13, color:G.good }}>−{fmt(saved)} finish time</span>
                    <span style={{ fontSize:12, color:"#888888", fontFamily:"'Oswald',sans-serif" }}>·</span>
                    <span style={{ fontSize:13, color:"#ccc", fontFamily:"'Oswald',sans-serif" }}>New projected finish: <span style={{ color:"#FFFFFF", fontFamily:"'Space Mono',monospace" }}>{fmtLong(result.total - saved)}</span></span>
                  </div>
                );
              })()}
            </div>

            {/* Per-run split targets */}
            <div style={{ background:"#181818", border:"1px solid #222", borderTop:"1px solid #2A2A2A", padding:"18px 22px", marginBottom:2 }}>
              <SLabel style={{ marginBottom:10 }}>Split Targets — Fatigue-Adjusted</SLabel>
              <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)", gap:2 }}>
                {result.runTargets.map(({run, time, note}) => {
                  const eliteTime = Math.round(result.eliteKm * FATIGUE[run-1]);
                  const isLast = run === 8;
                  return (
                    <div key={run} style={{ background:"#0C0C0C", border:`1px solid ${isLast?"#FF804044":"#222"}`, padding:"10px 12px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
                        <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.15em" }}>RUN {run}</span>
                        {isLast && <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.accentLight, letterSpacing:"0.1em" }}>FINAL</span>}
                      </div>
                      <div style={{ fontFamily:"'Space Mono',monospace", fontSize:16, color:"#FFFFFF", marginBottom:3 }}>{fmt(time)}</div>
                      <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:G.good, letterSpacing:"0.08em" }}>Top 10% {fmt(eliteTime)}</div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", marginTop:3 }}>{note}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop:8, fontSize:12, color:"#888888", fontFamily:"'Oswald',sans-serif", fontStyle:"italic" }}>
                Splits account for cumulative fatigue. Your R1 target is not your R8 target — plan accordingly.
              </div>
            </div>

          </div>

          {/* ── EMAIL CAPTURE ── */}
          {(() => {
            return (
              <div style={{ background:"#0f1318", border:"1px solid #303032", borderTop:`2px solid ${G.accent}`, padding:"24px 28px", marginBottom:2 }}>
                <div style={{ display:"flex", alignItems:"flex-start", gap:20, flexWrap:isMobile?"wrap":"nowrap" }}>
                  <div style={{ flex:1, minWidth:240 }}>
                    <div style={{ fontFamily:"'Anton',sans-serif", fontSize:18, letterSpacing:"0.04em", color:"#FFFFFF", marginBottom:6 }}>
                      GET YOUR FULL BREAKDOWN
                    </div>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:"#aaa", lineHeight:1.6 }}>
                      We'll email your projected finish, top 3 weak stations, and a personalised training tip. No account needed.
                    </div>
                  </div>
                  <EmailCapture result={result} div={div} gender={gender} age={age} ranked={
                    (() => {
                      const bms = result.benchmarks || getBenchmarks(div, gender, age);
                      return STATIONS.map((s,i) => ({ s, sec:result.stations[i], bm:bms[i], delta:result.stations[i]-bms[i].avg })).sort((a,b)=>b.delta-a.delta);
                    })()
                  } />
                </div>
              </div>
            );
          })()}

          {/* Share */}
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:0 }}>
            <ShareCard result={result} div={div} gender={gender} age={age} />
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

// ─── DERIVE ATHLETE PROFILE ──────────────────────────────────────────────────
// Infers physical limitations using relative delta scoring:
//   impactScore = (athleteTime - target) / target × RACE_POSITION_MULTIPLIER[id]
// Proportional gaps are more meaningful than raw seconds — a station 20% over target
// is a bigger signal than one that is 5% over even with a larger raw delta.
// confidence gates how many limitations are surfaced (high = 2, low/medium = 1).
function deriveAthleteProfile({
  stationsOffPace,
  timeGapPercent,
  runPaceVal,
  result,
  inputs,
  athleteLevel
}) {
  const bucketScores = {
    aerobic_threshold_support: 0,
    lower_body_fatigue_resistance: 0,
    upper_pull_posterior_endurance: 0,
    grip_trunk_endurance: 0,
    race_specific_recovery_into_running: 0,
  };

  const normalizedStations = (stationsOffPace || [])
    .map((s) => {
      const id = s.id || getStationIdFromName(s.name);
      if (!id) return null;
      const target = s.stationTarget || 1;
      const athleteTime = s.athleteTime || 0;
      const relativeDelta = target > 0 ? Math.max(0, (athleteTime - target) / target) : 0;
      const impactScore = relativeDelta * (RACE_POSITION_MULTIPLIER[id] || 1);
      return { ...s, id, relativeDelta, impactScore };
    })
    .filter(Boolean);

  for (const station of normalizedStations) {
    const weights = LIMITATION_WEIGHTS[station.id] || {};
    for (const [bucket, weight] of Object.entries(weights)) {
      bucketScores[bucket] += station.impactScore * weight;
    }
  }

  // Run-based adjustments using the app's actual data model.
  // result.km is the benchmark/default 1km run pace from calcRace.
  if (runPaceVal && result?.km && runPaceVal > result.km * 1.06) {
    bucketScores.aerobic_threshold_support += 0.18;
  }
  if (result?.runPct > 50 && normalizedStations.some(s =>
    ["sled_push","burpee","sandbag","wallball"].includes(s.id)
  )) {
    bucketScores.race_specific_recovery_into_running += 0.15;
  }
  if (timeGapPercent != null && timeGapPercent > 8) {
    bucketScores.aerobic_threshold_support += 0.10;
  }

  const ranked = Object.entries(bucketScores)
    .map(([key, score]) => ({ key, score: round2(score), label: LIMITATION_LABELS[key] }))
    .sort((a, b) => b.score - a.score);

  const confidence = getDataConfidence(inputs);
  const primaryCount   = confidence === "high" ? 2 : 1;
  const secondaryCount = confidence === "high" ? 2 : 1;

  const primary_limitations = ranked
    .slice(0, primaryCount)
    .filter(x => x.score > 0)
    .map(x => x.label);

  const secondary_limitations = ranked
    .slice(primaryCount, primaryCount + secondaryCount)
    .filter(x => x.score > 0)
    .map(x => x.label);

  const limitation_scores = ranked.reduce((acc, item) => {
    acc[item.key] = item.score;
    return acc;
  }, {});

  const training_bias = getTrainingBias(timeGapPercent, athleteLevel);

  // Fallback: always surface at least one primary limitation
  if (primary_limitations.length === 0) {
    const top = ranked[0];
    primary_limitations.push(top ? top.label : "general race-specific conditioning");
  }

  return {
    primary_limitations,
    secondary_limitations,
    limitation_scores,
    confidence,
    training_bias,
  };
}

// ─── TRAINING PLAN ────────────────────────────────────────────────────────────
function TrainingTab({ lastResult, highlightStation, onClearHighlight, onNavigate }) {
  const [plan, setPlan] = useState(null);
  const [savedProfile, setSavedProfile] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(0);

  useEffect(() => {
    // Reset plan when lastResult changes so stale plans don't show
    setPlan(null); setSavedProfile(null); setPlanError(null);
  }, [lastResult?.id]);

  if (!lastResult) return (
    <div style={{ background:"#0C0C0C", minHeight:"100vh" }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"32px 20px 80px" }}>
        <SLabel style={{ marginBottom:10 }}>Training Plan</SLabel>
        <div style={{ fontFamily:"'Anton',sans-serif", fontSize:"clamp(36px,6vw,60px)", lineHeight:0.92, textTransform:"uppercase", letterSpacing:"0.04em", color:"#FFFFFF", marginBottom:14 }}>
          Your Plan<br/><span style={{color:G.accent}}>Awaits.</span>
        </div>
        <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:16, color:"#888", lineHeight:1.6, maxWidth:440, marginBottom:32 }}>
          Run your first simulation. Your personalised 4-week plan will be generated from your actual simulation data.
        </p>
        <SBtn onClick={()=>onNavigate("sim")} style={{ padding:"16px 32px", fontSize:16 }}>→ Go to Simulator</SBtn>
      </div>
    </div>
  );

  const { result, div, gender, age } = lastResult;
  const trainingDays = lastResult.trainingDays || 4;
  const bms = result.benchmarks || getBenchmarks(div, gender||"male", age||"16");
  const ranked = STATIONS.map((s,i) => {
    const bm = bms[i];
    return { s, i, sec:result.stations[i], bm, gap:getGap(result.stations[i], bm.elite, bm.avg), delta:result.stations[i]-bm.avg };
  }).sort((a,b) => b.delta - a.delta);

  const tier = getTier(result.total);
  const athleteLevel = getAthleteLevelFromTier(tier.label);
  const runPaceVal = lastResult.runPace || RUN_DEFAULTS[div][gender||"male"];
  const runPaceFmt = fmt(runPaceVal) + "/km";
  const targetTimeFmt = lastResult.goal ? fmtLong(lastResult.goal) : "Not specified";
  const currentTimeFmt = fmtLong(result.total);

  const generatePlan = async () => {
    setPlanLoading(true);
    setPlanError(null);
    setPlan(null);

    // ── Build structured simulation data ──────────────────────────────────────
    // time_gap_percent: how far over target the current estimate is
    const goalSec = lastResult.goal;
    const timeGapPercent = goalSec
      ? Math.round(((result.total - goalSec) / goalSec) * 100)
      : null;

    // stations_off_pace: stations where athlete time exceeds their proportional
    // share of the target station time budget
    let stationsOffPace = [];
    if (goalSec) {
      const targetStationBudget = goalSec - result.totalRun - ROXZONE;
      const totalAvgStation = bms.reduce((a, b) => a + b.avg, 0);
      stationsOffPace = STATIONS.map((s, i) => {
        const athleteTime = result.stations[i];
        const stationWeight = bms[i].avg / totalAvgStation;
        const stationTarget = Math.round(stationWeight * targetStationBudget);
        const delta = athleteTime - stationTarget;
        return { id: s.id, name: s.name, athleteTime, stationTarget, delta };
      }).filter(s => s.delta > 0).sort((a, b) => b.delta - a.delta);
    } else {
      // No target set — fall back to division average gaps
      stationsOffPace = ranked.slice(0,3).map(w => ({
        id: w.s.id,
        name: w.s.name,
        athleteTime: w.sec,
        stationTarget: w.bm.avg,
        delta: Math.round(Math.abs(w.delta))
      }));
    }

    const athleteProfile = deriveAthleteProfile({
      stationsOffPace: stationsOffPace.slice(0,4),
      timeGapPercent,
      runPaceVal,
      result,
      inputs: lastResult.inputs || {},
      athleteLevel,
    });

    const simulationData = {
      time_gap_percent: timeGapPercent,
      stations_off_pace: stationsOffPace.slice(0,4).map((s) => ({
        name: s.name,
        delta_seconds: s.delta,
        current_time: fmt(s.athleteTime),
        target_time: fmt(s.stationTarget),
      })),
      derived_limitations: athleteProfile,
    };

    const stationsList = simulationData.stations_off_pace
      .map((s) => `${s.name} (gap: ${s.delta_seconds}s)`)
      .join(", ");
    const limitationScoreList = Object.entries(athleteProfile.limitation_scores)
      .map(([key, val]) => `${key}: ${val}`)
      .join("; ");

    const prompt = `You are a HYROX performance coach specializing in threshold-based hybrid training.
You must follow a structured reasoning process before generating the final plan.

ATHLETE PROFILE
Target time: ${targetTimeFmt}
Current time: ${currentTimeFmt}
Time gap (%): ${timeGapPercent !== null ? (timeGapPercent > 0 ? "+" : "") + timeGapPercent + "%" : "No target set"}
Training availability: ${trainingDays} days per week
Athlete level: ${athleteLevel}

PERFORMANCE LIMITATIONS (PRE-ANALYZED)
Primary: ${athleteProfile.primary_limitations.join("; ") || "none"}
Secondary: ${athleteProfile.secondary_limitations.join("; ") || "none"}
Confidence: ${athleteProfile.confidence}
Limitation scores: ${limitationScoreList}

TRAINING BIAS
Focus: ${athleteProfile.training_bias.focus}
Complexity: ${athleteProfile.training_bias.complexity}
Density progression: ${athleteProfile.training_bias.density_progression}

Weak stations (for context only, do not isolate): ${stationsList}

TASK
You must complete the following steps IN ORDER:

STEP 1: INTERPRETATION
Briefly explain (3-5 bullet points):
* What is actually limiting this athlete's performance
* How these limitations show up during a HYROX race
* If confidence is low or medium, be careful not to overstate certainty

STEP 2: TRAINING STRATEGY
Define the programming approach:
* What type of threshold work is needed
* How hybrid sessions will address the limitations
* How progression will occur over 4 weeks
* Follow the stated training bias

STEP 3: TRAINING PLAN (MAIN OUTPUT)
Generate a 4-week HYROX training plan.
STRICT REQUIREMENTS:
* ${trainingDays} training days per week
* Each week must include:
   * 1 threshold run session
   * 1 hybrid threshold session (key workout)
   * 1 strength/support session
   * 1 aerobic/base session
* All weaknesses must be trained within combined sessions
* Do NOT isolate stations into separate days
* Maintain threshold intent (not max effort conditioning)
* Use weak stations as signals of underlying limitations, not as isolated programming targets
* If confidence is low, favor broader threshold development over highly specific station prescriptions

Each training day must include:
* Session name
* Objective (1 sentence)
* Full workout (sets, reps, distances, rest)
* Intensity guidance (RPE or pacing)

STEP 4: PROGRESSION SUMMARY
Explain how the plan evolves from Week 1 to Week 4.

OUTPUT STYLE
* Clear, structured, app-friendly
* No fluff
* No motivational language
* No randomness
* Workouts must be repeatable and logical

CRITICAL RULE
This is a threshold-based HYROX program.
Do NOT:
* Create random circuits
* Overload strength at the expense of pacing
* Ignore running quality
* Program max-effort workouts

OUTPUT FORMAT (respond with a JSON object only, no markdown fences):
{
  "interpretation": ["bullet 1", "bullet 2", "bullet 3"],
  "strategy": "2-3 sentences on programming approach",
  "weeks": [
    {
      "week": 1,
      "theme": "one sentence describing this week's focus",
      "days": [
        {
          "name": "Session name",
          "type": "Threshold Run | Hybrid Threshold | Strength | Aerobic Base | Recovery",
          "objective": "One sentence objective",
          "workout": "Full workout detail with sets, reps, distances, rest",
          "intensity": "RPE or pacing guidance"
        }
      ]
    }
  ],
  "progression_summary": "2-3 sentences describing how the plan evolves week to week"
}`;

    try {
      const response = await fetch("/api/generate-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5",
          max_tokens: 4000,
          messages: [{ role: "user", content: prompt }]
        })
      });
      const data = await response.json();
      const raw = data.content?.find(b => b.type === "text")?.text || "";
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setPlan(parsed);
      setSavedProfile(athleteProfile);
      setExpandedWeek(0);
    } catch(e) {
      setPlanError("Failed to generate plan. Please try again.");
    } finally {
      setPlanLoading(false);
    }
  };

  const typeColor = (type) => {
    if (type?.includes("Hybrid")) return "#F2620F";
    if (type?.includes("Threshold")) return "#00aaff";
    if (type?.includes("Strength")) return "#aadd00";
    if (type?.includes("Aerobic")) return "#00e87a";
    return "#555";
  };

  return (
    <div style={{ background:"#0C0C0C", minHeight:"100vh" }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"32px 20px 80px" }}>

        {/* Header */}
        <SLabel style={{ marginBottom:10 }}>Training Plan</SLabel>
        <div style={{ fontFamily:"'Anton',sans-serif", fontSize:"clamp(32px,5vw,54px)", lineHeight:0.92, textTransform:"uppercase", letterSpacing:"0.04em", color:"#FFFFFF", marginBottom:14 }}>
          4-Week<br/><span style={{color:G.accent}}>Plan.</span>
        </div>

        {/* Context strip */}
        <div style={{ borderBottom:"1px solid #161616", paddingBottom:"22px", marginBottom:"22px", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:0 }}>
          <div style={{ borderRight:"1px solid #161616", paddingRight:16 }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.22em", textTransform:"uppercase", marginBottom:6 }}>Current</div>
            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:20, color:"#FFFFFF" }}>{currentTimeFmt}</div>
          </div>
          <div style={{ borderRight:"1px solid #161616", padding:"0 16px" }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.22em", textTransform:"uppercase", marginBottom:6 }}>Target</div>
            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:20, color: lastResult.goal ? "#FFFFFF" : "#282828" }}>{targetTimeFmt}</div>
          </div>
          <div style={{ paddingLeft:16 }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.22em", textTransform:"uppercase", marginBottom:6 }}>Days/Wk</div>
            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:20, color:"#FFFFFF" }}>{trainingDays}</div>
          </div>
        </div>

        {/* Targeting — target-relative station gaps */}
        {(() => {
          const goalSec = lastResult.goal;
          const timeGapPercent = goalSec
            ? Math.round(((result.total - goalSec) / goalSec) * 100)
            : null;
          const targetStationBudget = goalSec ? goalSec - result.totalRun - ROXZONE : null;
          const totalAvgStation = bms.reduce((a, b) => a + b.avg, 0);
          const offPace = goalSec
            ? STATIONS.map((s, i) => {
                const athleteTime = result.stations[i];
                const stationWeight = bms[i].avg / totalAvgStation;
                const stationTarget = Math.round(stationWeight * targetStationBudget);
                const delta = athleteTime - stationTarget;
                return { s, i, athleteTime, stationTarget, delta, gap: getGap(athleteTime, bms[i].elite, bms[i].avg) };
              }).filter(s => s.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 3)
            : ranked.slice(0, 3).map(w => ({ ...w, stationTarget: w.bm.avg, delta: Math.abs(w.delta) }));

          return (
            <div style={{ marginBottom:24 }}>
              {timeGapPercent !== null && (
                <div style={{ background:"#181818", border:"1px solid #252528", borderLeft:`3px solid ${timeGapPercent > 0 ? G.danger : G.good}`, padding:"12px 16px", marginBottom:2 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.2em", textTransform:"uppercase" }}>Time Gap to Target</div>
                    <div style={{ fontFamily:"'Space Mono',monospace", fontSize:16, color: timeGapPercent > 0 ? G.danger : G.good }}>
                      {timeGapPercent > 0 ? "+" : ""}{timeGapPercent}%
                    </div>
                  </div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", marginTop:4, letterSpacing:"0.06em" }}>
                    {fmtLong(result.total)} current · {fmtLong(goalSec)} target · {fmtLong(Math.abs(result.total - goalSec))} {timeGapPercent > 0 ? "to find" : "ahead"}
                  </div>
                </div>
              )}
              <SLabel style={{ marginBottom:8, marginTop: timeGapPercent !== null ? 16 : 0 }}>
                {goalSec ? "Stations Off Target Pace" : "Weakest Stations"}
              </SLabel>
              <div style={{ display:"grid", gap:2 }}>
                {offPace.map(({s, athleteTime, stationTarget, delta, gap}) => (
                  <div key={s.id} style={{ background:"#181818", border:"1px solid #252528", borderLeft:`3px solid ${gap.color}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:13, letterSpacing:"0.12em", textTransform:"uppercase", color:"#FFFFFF" }}>{s.name}</div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", marginTop:2, letterSpacing:"0.06em" }}>
                        {goalSec
                          ? `Current: ${fmt(athleteTime)} · Target: ${fmt(stationTarget)} · +${delta}s off pace`
                          : `Current: ${fmt(athleteTime)} · +${fmt(delta)} over div avg`}
                      </div>
                    </div>
                    <STierIcon label={gap.label} color={gap.color} />
                  </div>
                ))}
                <div style={{ background:"#181818", border:"1px solid #252528", borderLeft:"3px solid #00aaff", padding:"12px 16px" }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:13, letterSpacing:"0.12em", textTransform:"uppercase", color:"#FFFFFF" }}>1km Run Pace</div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", marginTop:2, letterSpacing:"0.06em" }}>
                    Current: {runPaceFmt} · integrated across all sessions
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Generate / Plan display */}
        {!plan && !planLoading && (() => {
          // Pre-compute limitations for display before generating
          const goalSec2 = lastResult.goal;
          const tgp2 = goalSec2 ? Math.round(((result.total - goalSec2) / goalSec2) * 100) : null;
          const targetBudget2 = goalSec2 ? goalSec2 - result.totalRun - ROXZONE : null;
          const totalAvg2 = bms.reduce((a,b) => a + b.avg, 0);
          const offPace2 = goalSec2
            ? STATIONS.map((s,i) => {
                const at = result.stations[i];
                const tgt = Math.round((bms[i].avg / totalAvg2) * targetBudget2);
                return { id: s.id, name: s.name, athleteTime: at, stationTarget: tgt, delta: at - tgt };
              }).filter(s => s.delta > 0).sort((a,b) => b.delta - a.delta).slice(0,3)
            : ranked.slice(0,3).map(w => ({ id: w.s.id, name: w.s.name, athleteTime: w.sec, stationTarget: w.bm.avg, delta: Math.abs(w.delta) }));
          const profile = deriveAthleteProfile({
            stationsOffPace: offPace2,
            timeGapPercent: tgp2,
            runPaceVal,
            result,
            inputs: lastResult.inputs || {},
            athleteLevel,
          });

          return (
            <div>
              {/* Derived limitations preview */}
              <div style={{ background:"#0F1318", border:"1px solid #1E2530", borderLeft:"3px solid #F2620F", padding:"16px 18px", marginBottom:2 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F2620F", letterSpacing:"0.2em", textTransform:"uppercase" }}>Derived Limitations</div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.14em", textTransform:"uppercase", padding:"2px 8px",
                    color: profile.confidence==="high"?"#00e87a":profile.confidence==="medium"?"#ffaa00":"#888",
                    background: profile.confidence==="high"?"rgba(0,232,122,0.08)":profile.confidence==="medium"?"rgba(255,170,0,0.08)":"#1A1A1A"
                  }}>
                    {profile.confidence} confidence
                  </div>
                </div>
                <div style={{ display:"grid", gap:6, marginBottom:12 }}>
                  {profile.primary_limitations.map((l,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.14em", textTransform:"uppercase", color:"#F2620F", background:"rgba(242,98,15,0.1)", padding:"2px 7px", flexShrink:0, marginTop:1 }}>Primary</div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, color:"#FFFFFF", letterSpacing:"0.05em" }}>{l}</div>
                    </div>
                  ))}
                  {profile.secondary_limitations.map((l,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.14em", textTransform:"uppercase", color:"#555", background:"#1A1A1A", padding:"2px 7px", flexShrink:0, marginTop:1 }}>Secondary</div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, color:"#888", letterSpacing:"0.05em" }}>{l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop:"1px solid #1E2530", paddingTop:10 }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#333", letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:3 }}>Programming Directive</div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.05em" }}>{profile.training_bias?.focus}</div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#2E2E2E", letterSpacing:"0.08em", marginTop:4 }}>
                    Complexity: {profile.training_bias?.complexity} · Progression: {profile.training_bias?.density_progression}
                  </div>
                </div>
              </div>

              {planError && (
                <div style={{ background:"rgba(242,98,15,0.08)", border:"1px solid rgba(242,98,15,0.2)", padding:"12px 16px", marginBottom:2, fontFamily:"'Oswald',sans-serif", fontSize:13, color:"#F2620F", letterSpacing:"0.06em" }}>
                  {planError}
                </div>
              )}
              <div onClick={generatePlan} style={{ background:"#F2620F", padding:"22px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}>
                <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:"#fff" }}>
                  Generate My 4-Week Plan
                </span>
                <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:20, color:"#fff" }}>→</span>
              </div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#333", letterSpacing:"0.08em", marginTop:8, textAlign:"center" }}>
                AI-generated · threshold-based · limitation-focused
              </div>
            </div>
          );
        })()}

        {planLoading && (
          <div style={{ background:"#181818", border:"1px solid #252528", padding:"40px 20px", textAlign:"center" }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, color:"#444", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:12 }}>Building your plan</div>
            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:"#333" }}>Deriving limitations · designing sessions · structuring progression</div>
          </div>
        )}

        {plan && (
          <div>

            {/* Step 1: Interpretation */}
            {plan.interpretation?.length > 0 && (
              <div style={{ background:"#0F1318", border:"1px solid #1E2530", padding:"16px 18px", marginBottom:2 }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:12 }}>Step 1 · Interpretation</div>
                <div style={{ display:"grid", gap:8 }}>
                  {plan.interpretation.map((point, i) => (
                    <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                      <span style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:"#F2620F", flexShrink:0, marginTop:2 }}>—</span>
                      <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, color:"#888", lineHeight:1.5, letterSpacing:"0.04em" }}>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Strategy */}
            {plan.strategy && (
              <div style={{ background:"#0F1318", border:"1px solid #1E2530", padding:"16px 18px", marginBottom:2 }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:8 }}>Step 2 · Training Strategy</div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, color:"#888", lineHeight:1.65, letterSpacing:"0.04em" }}>{plan.strategy}</div>
              </div>
            )}

            {/* Step 3: Week tabs */}
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6, marginTop:16 }}>Step 3 · 4-Week Plan</div>
            <div style={{ display:"flex", gap:2, marginBottom:2 }}>
              {plan.weeks?.map((w,i) => (
                <div key={i} onClick={()=>setExpandedWeek(i)} style={{
                  flex:1, padding:"10px 8px", textAlign:"center",
                  background: expandedWeek===i ? "#F2620F" : "#181818",
                  border:`1px solid ${expandedWeek===i?"#F2620F":"#252528"}`,
                  cursor:"pointer"
                }}>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, fontWeight:700, letterSpacing:"0.12em", color: expandedWeek===i?"#fff":"#555", textTransform:"uppercase" }}>Wk {w.week}</div>
                </div>
              ))}
            </div>

            {/* Week content */}
            {plan.weeks && plan.weeks[expandedWeek] && (() => {
              const week = plan.weeks[expandedWeek];
              return (
                <div style={{ background:"#181818", border:"1px solid #252528", borderTop:"none" }}>
                  {week.theme && (
                    <div style={{ padding:"12px 18px", borderBottom:"1px solid #222", background:"#141414" }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:3 }}>Week {week.week} Focus</div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:"#aaa", letterSpacing:"0.05em" }}>{week.theme}</div>
                    </div>
                  )}
                  {week.days?.map((day, di) => (
                    <div key={di} style={{ borderBottom:"1px solid #1A1A1A" }}>
                      <div style={{ padding:"14px 18px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.18em", textTransform:"uppercase", color:typeColor(day.type), background:`${typeColor(day.type)}15`, padding:"2px 8px", flexShrink:0 }}>{day.type}</div>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:14, letterSpacing:"0.1em", textTransform:"uppercase", color:"#FFFFFF" }}>{day.name}</div>
                        </div>
                        {day.objective && (
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#666", letterSpacing:"0.06em", marginBottom:10, lineHeight:1.4 }}>{day.objective}</div>
                        )}
                        <div style={{ background:"#0C0C0C", border:"1px solid #1E1E1E", padding:"12px 14px", marginBottom:8 }}>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:6 }}>Workout</div>
                          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, color:"#ccc", lineHeight:1.7, letterSpacing:"0.04em", whiteSpace:"pre-wrap" }}>{day.workout}</div>
                        </div>
                        {day.intensity && (
                          <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                            <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F2620F", letterSpacing:"0.18em", textTransform:"uppercase", flexShrink:0, marginTop:1 }}>Intensity</span>
                            <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.06em", lineHeight:1.4 }}>{day.intensity}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Step 4: Progression Summary */}
            {plan.progression_summary && (
              <div style={{ background:"#0F1318", border:"1px solid #1E2530", padding:"16px 18px", marginTop:2 }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#555", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:8 }}>Step 4 · Progression Summary</div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, color:"#888", lineHeight:1.65, letterSpacing:"0.04em" }}>{plan.progression_summary}</div>
              </div>
            )}

            {/* Diagnosis confidence footnote */}
            {savedProfile && (
              <div style={{ background:"#0C0C0C", border:"1px solid #141414", padding:"12px 18px", marginTop:2, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#333", letterSpacing:"0.14em", textTransform:"uppercase" }}>Diagnosis Confidence</div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, fontWeight:600, letterSpacing:"0.14em", textTransform:"uppercase", padding:"2px 8px",
                  color: savedProfile.confidence==="high" ? "#00e87a" : savedProfile.confidence==="medium" ? "#ffaa00" : "#888",
                  background: savedProfile.confidence==="high" ? "rgba(0,232,122,0.08)" : savedProfile.confidence==="medium" ? "rgba(255,170,0,0.08)" : "#1A1A1A"
                }}>
                  {savedProfile.confidence}
                </div>
              </div>
            )}

            {/* Regenerate */}
            <div onClick={generatePlan} style={{ background:"#181818", border:"1px solid #252528", padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", marginTop:2 }}>
              <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.16em", textTransform:"uppercase", color:"#444" }}>Regenerate Plan</span>
              <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:16, color:"#333" }}>↺</span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── PROGRESS ─────────────────────────────────────────────────────────────────
function ProgressTab({ entries, setEntries, onNavigate }) {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const isMobile = useIsMobile(); // eslint-disable-line no-unused-vars

  const handleDelete = async (id) => {
    const updated = entries.filter(e=>e.id!==id);
    await saveEntries(updated); setEntries(updated); setConfirmDelete(null);
  };

  if (entries.length===0) return (
    <div style={{ background:"#0C0C0C", minHeight:"100vh" }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"32px 20px 80px" }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"#444", textTransform:"uppercase", marginBottom:8 }}>My Progress</div>
        <div style={{ fontFamily:"'Space Mono',monospace", fontSize:34, color:"#282828", letterSpacing:"0.04em", marginBottom:6 }}>—:——:——</div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em", marginBottom:32 }}>No simulations saved yet</div>
        <div style={{ background:"#F2620F", padding:"11px 20px", marginBottom:0 }}>
          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:"#000" }}>Get Started</div>
        </div>
        {STATIONS.slice(0,4).map(s => (
          <div key={s.id} style={{ display:"grid", gridTemplateColumns:"1fr auto", alignItems:"center", padding:"13px 0", borderBottom:"1px solid #141414", opacity:0.25 }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:600, letterSpacing:"0.14em", textTransform:"uppercase", color:"#FFFFFF" }}>{s.name}</div>
            <div style={{ fontFamily:"'Space Mono',monospace", fontSize:13, color:"#444" }}>—:——</div>
          </div>
        ))}
        <div onClick={()=>onNavigate("sim")} style={{ background:"#F2620F", padding:"22px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}>
          <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, fontWeight:700, letterSpacing:"0.22em", textTransform:"uppercase", color:"#FFFFFF" }}>Run My First Simulation</span>
          <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:20, color:"#FFFFFF" }}>→</span>
        </div>
      </div>
    </div>
  );

  const sorted = [...entries].sort((a,b)=>a.id-b.id);
  const latest = sorted[sorted.length-1];
  const first = sorted[0];
  const improvement = sorted.length>1 ? first.result.total - latest.result.total : 0;

  const latestBms = latest.result.benchmarks || getBenchmarks(latest.div, latest.gender||"male", latest.age||"16");
  const stationTrends = STATIONS.map((s,i) => {
    const times = sorted.map(e=>e.result.stations[i]);
    const delta = times[0]-times[times.length-1];
    return { s, times, delta, latestT:times[times.length-1], bm:latestBms[i] };
  });

  return (
    <div style={{ background:"#0C0C0C", minHeight:"100vh" }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"32px 20px 80px" }}>
      <div style={{ borderBottom:"1px solid #161616", paddingBottom:"22px", marginBottom:"22px" }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"#444", textTransform:"uppercase", marginBottom:8 }}>
          <span style={{ display:"inline-block", width:5, height:5, background:"#F2620F", borderRadius:"50%", marginRight:7, verticalAlign:"middle" }}/>
          My Progress
        </div>
        <div style={{ fontFamily:"'Space Mono',monospace", fontSize:34, color:"#FFFFFF", letterSpacing:"0.04em", lineHeight:1 }}>
          {sorted.length} {sorted.length===1?"Entry":"Entries"}
        </div>
        {improvement>0&&sorted.length>1&&(
          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em", marginTop:6 }}>
            <span style={{ color:"#F2620F" }}>−{fmt(improvement)}</span> saved since baseline
          </div>
        )}
      </div>

      {/* Readiness row */}
      {(() => {
        const lr = latest;
        const rr = calcReadiness(lr.result, lr.inputs||{});
        const prev = sorted.length > 1 ? sorted[sorted.length-2] : null;
        const prevRr = prev ? calcReadiness(prev.result, prev.inputs||{}) : null;
        const scoreDelta = prevRr ? rr.score - prevRr.score : null;
        return (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1px 1fr", borderBottom:"1px solid #161616", paddingBottom:"22px", marginBottom:"22px" }}>
            <div style={{ paddingRight:16 }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.22em", textTransform:"uppercase", marginBottom:6 }}>Race Readiness</div>
              <div style={{ fontFamily:"'Space Mono',monospace", fontSize:28, color:rr.color, letterSpacing:"0.04em" }}>{rr.score}<span style={{ fontSize:14, color:"#333" }}>/100</span></div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em", marginTop:4 }}>{rr.label}{scoreDelta!==null&&<span style={{ color:scoreDelta>0?G.good:G.danger, marginLeft:8 }}>{scoreDelta>0?"+":""}{scoreDelta} pts</span>}</div>
            </div>
            <div style={{ background:"#161616" }}/>
            <div style={{ paddingLeft:16 }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.22em", textTransform:"uppercase", marginBottom:6 }}>Latest</div>
              <div style={{ fontFamily:"'Space Mono',monospace", fontSize:28, color:"#FFFFFF", letterSpacing:"0.04em" }}>{fmtLong(latest.result.total)}</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em", marginTop:4 }}>
                {improvement>0?<span style={{ color:"#F2620F" }}>−{fmt(improvement)} vs baseline</span>:"baseline entry"}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Simulation History */}
      <div style={{ background:"#F2620F", padding:"11px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:0 }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:"#000" }}>Simulation History</div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, fontWeight:600, letterSpacing:"0.12em", color:"#7A2A00" }}>{sorted.length} {sorted.length===1?"entry":"entries"}</div>
      </div>
      <div style={{ marginBottom:0 }}>
        {sorted.map((e,idx) => {
          const isLatest = idx===sorted.length-1;
          const prev = idx>0?sorted[idx-1]:null;
          const delta = prev ? prev.result.total - e.result.total : null;
          const tier = getTier(e.result.total);
          const dateStr = new Date(e.date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"2-digit"});
          return (
            <div key={e.id} style={{ borderBottom:"1px solid #141414" }}>
              <div onClick={()=>setExpandedEntry(expandedEntry===e.id ? null : e.id)}
                style={{ display:"grid", gridTemplateColumns:"1fr auto auto auto", alignItems:"center", gap:12, padding:"14px 0", cursor:"pointer" }}>
                <div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color: isLatest?"#F2620F":"#444", letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:3 }}>
                    {dateStr}{isLatest?" · Latest":""}{idx===0&&sorted.length>1?" · Baseline":""}
                  </div>
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:20, color: isLatest?"#FFFFFF":"#888" }}>{fmtLong(e.result.total)}</div>
                  <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#333", letterSpacing:"0.1em", marginTop:3 }}>
                    {DIV_LABELS[e.div]} · {e.gender==="female"?"Female":"Male"}{e.goal?` · Target ${fmtLong(e.goal)}`:""}
                  </div>
                </div>
                {delta!==null&&(
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:delta>0?G.good:G.danger, textAlign:"center" }}>
                    {delta>0?`−${fmt(delta)}`:`+${fmt(Math.abs(delta))}`}
                  </div>
                )}
                <div style={{ display:"inline-block" }}><STierIcon label={tier.label} color={tier.color} size={18} /></div>
                <div onClick={ev=>{ev.stopPropagation();setConfirmDelete(e.id);}}
                  style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#333", letterSpacing:"0.1em", cursor:"pointer", padding:"4px 0" }}>✕</div>
              </div>
              {expandedEntry===e.id&&(
                <div style={{ borderTop:"1px solid #141414", paddingTop:16, paddingBottom:16 }}>
                  <ResultCard result={e.result} div={e.div} gender={e.gender} age={e.age} />
                  <div style={{ display:"flex", justifyContent:"flex-end", marginTop:12 }}>
                    <ShareCard result={e.result} div={e.div} gender={e.gender} age={e.age} />
                  </div>
                </div>
              )}
              {confirmDelete===e.id&&(
                <div style={{ paddingBottom:14, display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:12, color:"#888", flex:1, fontFamily:"'Oswald',sans-serif", letterSpacing:"0.05em" }}>Delete this entry?</span>
                  <SBtn variant="danger" onClick={()=>handleDelete(e.id)} style={{ padding:"7px 12px", fontSize:12 }}>Delete</SBtn>
                  <SBtn variant="ghost" onClick={()=>setConfirmDelete(null)} style={{ padding:"7px 12px", fontSize:12 }}>Cancel</SBtn>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Station trends */}
      {sorted.length>1&&(
        <>
          <div style={{ background:"#F2620F", padding:"11px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:0 }}>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:"#000" }}>Station Trends</div>
            <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, fontWeight:600, letterSpacing:"0.12em", color:"#7A2A00" }}>First vs Latest</div>
          </div>
          <div style={{ marginBottom:0 }}>
            {stationTrends.sort((a,b)=>b.delta-a.delta).map(({s, times, delta, latestT, bm}) => {
              const gap = getGap(latestT, bm.elite, bm.avg);
              const maxT = Math.max(...times);
              return (
                <div key={s.id} style={{ display:"grid", gridTemplateColumns:"1fr auto auto", alignItems:"center", gap:12, padding:"13px 0", borderBottom:"1px solid #141414" }}>
                  <div>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:600, letterSpacing:"0.14em", textTransform:"uppercase", color:"#FFFFFF" }}>{s.name}</div>
                    <div style={{ display:"flex", alignItems:"flex-end", gap:2, height:16, marginTop:6 }}>
                      {times.map((t,i) => {
                        const h = Math.max(2, Math.round((1-(t/maxT))*12)+2);
                        return <div key={i} style={{ width:6, height:h, background:i===times.length-1?"#F2620F":gap.color+"44" }}/>;
                      })}
                    </div>
                  </div>
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:delta>0?G.good:delta<0?G.danger:"#444", textAlign:"right" }}>
                    {delta>0?`−${fmt(delta)}`:delta<0?`+${fmt(Math.abs(delta))}`:"±0"}
                  </div>
                  <div style={{ display:"inline-block" }}><STierIcon label={gap.label} color={gap.color} size={16} /></div>
                </div>
              );
            })}
          </div>

          {/* Callout row */}
          {(() => {
            const bestImproved = stationTrends.filter(t=>t.delta>0).sort((a,b)=>b.delta-a.delta)[0];
            const mostNeeded = stationTrends.filter(t=>t.latestT>t.bm.avg).sort((a,b)=>(b.latestT-b.bm.avg)-(a.latestT-a.bm.avg))[0];
            if (!bestImproved && !mostNeeded) return null;
            return (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1px 1fr", borderTop:"1px solid #161616", marginTop:0 }}>
                {bestImproved&&(
                  <div style={{ padding:"16px 0 16px", paddingRight:16, borderTop:"3px solid "+G.good }}>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.good, letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:6 }}>Best Improvement</div>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:14, color:"#FFFFFF", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:3 }}>{bestImproved.s.name}</div>
                    <div style={{ fontFamily:"'Space Mono',monospace", fontSize:13, color:G.good }}>−{fmt(bestImproved.delta)}</div>
                  </div>
                )}
                {bestImproved&&mostNeeded&&<div style={{ background:"#161616" }}/>}
                {mostNeeded&&(
                  <div style={{ padding:"16px 0 16px", paddingLeft:bestImproved?16:0, borderTop:"3px solid "+G.danger }}>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.danger, letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:6 }}>Still Needs Work</div>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:14, color:"#FFFFFF", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:3 }}>{mostNeeded.s.name}</div>
                    <div style={{ fontFamily:"'Space Mono',monospace", fontSize:13, color:G.danger }}>+{fmt(mostNeeded.latestT-mostNeeded.bm.avg)} vs avg</div>
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* ── PERFORMANCE LEGEND ── */}
      <div style={{ marginTop:60 }}>
        <SLabel muted style={{ marginBottom:16 }}>Performance Legend</SLabel>
        <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:"#aaa", marginBottom:20, lineHeight:1.6 }}>
          All performance bands are relative to your <span style={{ color:"#FFFFFF" }}>division + age group</span> — not absolute times.
          A 55-year-old finishing in 1:30 is Competitive in their age group. A 25-year-old finishing in 1:30 is Advanced.
          Benchmarks are based on aggregated global HYROX race results.
        </p>

        {/* Station performance bands */}
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>Station Performance Bands</div>
        <div style={{ display:"grid", gap:2, marginBottom:28 }}>
          {[
            { label:"Podium",      color:"#00e87a", position:"Top 10%",       desc:"Age-group podium contender. Station is a genuine race weapon." },
            { label:"Competitive", color:"#aadd00", position:"Top 10–25%",    desc:"Well above division average. Solid strength here — maintain it." },
            { label:"Advanced",    color:"#00aaff", position:"Top 25–50%",    desc:"Above average. Some gains available but not a priority." },
            { label:"Average",     color:"#ffaa00", position:"Top 50–70%",    desc:"At or near division average. Targeted training will move this up." },
            { label:"Building",    color:"#ff3c00", position:"Bottom 30%",    desc:"Below division average. This station is costing you time. Train it." },
          ].map(({ label, color, position, desc }) => (
            <div key={label} style={{ display:"grid", gridTemplateColumns:"auto 90px 80px 1fr", alignItems:"center", gap:16, background:"#242426", border:"1px solid #303032", borderLeft:`3px solid ${color}`, padding:"12px 18px" }}>
              <STierIcon label={label} color={color} size={18} />
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color, letterSpacing:"0.1em", fontWeight:700 }}>{label}</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.05em" }}>{position}</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:"#ccc", lineHeight:1.4 }}>{desc}</div>
            </div>
          ))}
        </div>

        {/* Finish time tiers */}
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>Finish Time Tiers</div>
        <div style={{ display:"grid", gap:2, marginBottom:28 }}>
          {[
            { label:"Podium",     color:"#00e87a", bench:"Men Open 25–29: sub 1:04",     desc:"Top 10% of your division + age group. Competing for age-group wins and World Championship qualification." },
            { label:"Competitive",color:"#aadd00", bench:"Men Open 25–29: 1:04–1:08",   desc:"Top 25%. Strong athlete with a competitive background. Racing to place well and beat your peers." },
            { label:"Advanced",   color:"#00aaff", bench:"Men Open 25–29: 1:08–1:20",   desc:"Top 50%. Above average. You've built a HYROX-specific base and are consistently improving." },
            { label:"Average",    color:"#ffaa00", bench:"Men Open 25–29: 1:20–1:32",   desc:"Bottom 50%. Typical recreational competitor. Targeted training will move you up quickly." },
            { label:"Building",   color:"#aaaaaa", bench:"Men Open 25–29: 1:32+",       desc:"Bottom 25%. Early-stage or first-timer. Every session builds the foundation — significant gains ahead." },
          ].map(({ label, color, bench, desc }) => (
            <div key={label} style={{ display:"grid", gridTemplateColumns:"auto 90px 1fr 1fr", alignItems:"center", gap:16, background:"#242426", border:"1px solid #303032", borderLeft:`3px solid ${color}`, padding:"12px 18px" }}>
              <STierIcon label={label} color={color} size={18} />
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color, letterSpacing:"0.1em", fontWeight:700 }}>{label}</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.05em", lineHeight:1.5 }}>{bench}</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:"#ccc", lineHeight:1.4 }}>{desc}</div>
            </div>
          ))}
        </div>

        {/* Benchmark terms */}
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#888888", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>Benchmark Terms</div>
        <div style={{ display:"grid", gap:2, marginBottom:16 }}>
          {[
            { term:"Div. Avg",   desc:"The average time for your division + age group at this station. Based on global HYROX race results." },
            { term:"Top 10%",    desc:"The time the fastest 10% of athletes in your division + age group achieve at this station. This is your stretch target, not a professional benchmark." },
            { term:"Run pace",   desc:"Your average 1km pace across all 8 runs. Estimated from division defaults if not entered — update it for a more accurate prediction." },
          ].map(({ term, desc }) => (
            <div key={term} style={{ display:"grid", gridTemplateColumns:"100px 1fr", alignItems:"center", gap:16, background:"#242426", border:"1px solid #303032", padding:"12px 18px" }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.accentLight, letterSpacing:"0.1em", fontWeight:700 }}>{term}</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, color:"#ccc", lineHeight:1.4 }}>{desc}</div>
            </div>
          ))}
        </div>

        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.1em", lineHeight:1.8 }}>
          Data source: Aggregated global HYROX race results · HyroxDataLab analysis (5,000+ athletes, 2025) · Updated February 2026
        </div>
      </div>
    </div>
    </div>
  );
}


// ─── POST-RACE DEBRIEF ────────────────────────────────────────────────────────
function DebriefTab({ lastResult }) {
  const [actuals, setActuals] = useState({});
  const [actualRunMin, setActualRunMin] = useState("");
  const [actualRunSec, setActualRunSec] = useState("");
  const [debriefResult, setDebriefResult] = useState(null);

  const isMobile = useIsMobile(); // eslint-disable-line no-unused-vars
  if (!lastResult) return (
    <div style={{ background:"#0C0C0C", minHeight:"100vh" }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"32px 20px 80px" }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"#444", textTransform:"uppercase", marginBottom:8 }}>Race Debrief</div>
        <div style={{ fontFamily:"'Space Mono',monospace", fontSize:34, color:"#282828", letterSpacing:"0.04em", marginBottom:6 }}>—:——:——</div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em" }}>Run the simulator first, then return after your race</div>
      </div>
    </div>
  );

  const { result, div, gender, age } = lastResult;
  const bms = result.benchmarks || getBenchmarks(div, gender||"male", age||"16");
  const setActual = (id, v) => setActuals(p => ({...p,[id]:v}));
  const actualRunPace = actualRunMin ? (parseInt(actualRunMin)||0)*60+(parseInt(actualRunSec)||0) : null;

  const analyzeRace = () => {
    const stationActuals = STATIONS.map(s => {
      const t = s.calc(actuals[s.id]);
      return t != null ? t : null;
    });
    const actualRunTotal = actualRunPace ? FATIGUE.map(f=>Math.round(actualRunPace*f)).reduce((a,b)=>a+b,0) : null;
    const projectedTotal = result.total;
    const filledActuals = stationActuals.map((t,i) => t != null ? t : result.stations[i]);
    const actualStationTotal = filledActuals.reduce((a,b)=>a+b,0);
    const actualTotal = (actualRunTotal || result.totalRun) + actualStationTotal + ROXZONE;
    const accuracy = Math.round((1 - Math.abs(actualTotal - projectedTotal) / projectedTotal) * 100);
    const stationDeltas = STATIONS.map((s,i) => ({
      s,
      predicted: result.stations[i],
      actual: stationActuals[i],
      delta: stationActuals[i] != null ? stationActuals[i] - result.stations[i] : null,
      bm: bms[i]
    }));
    const runDelta = actualRunPace ? actualRunPace - result.km : null;
    setDebriefResult({ actualTotal, projectedTotal, accuracy, stationDeltas, actualRunPace, runDelta, actualRunTotal, actualStationTotal });
  };

  return (
    <div style={{ background:"#0C0C0C", minHeight:"100vh" }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"32px 20px 80px" }}>

      {/* Predicted baseline — large display */}
      <div style={{ borderBottom:"1px solid #161616", padding:"22px 0" }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"#444", textTransform:"uppercase", marginBottom:8 }}>
          <span style={{ display:"inline-block", width:5, height:5, background:"#F2620F", borderRadius:"50%", marginRight:7, verticalAlign:"middle" }}/>
          Predicted Finish
        </div>
        <div style={{ fontFamily:"'Space Mono',monospace", fontSize:34, color:"#FFFFFF", letterSpacing:"0.04em", lineHeight:1 }}>{fmtLong(result.total)}</div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em", marginTop:6 }}>
          {DIV_LABELS[div]} · {gender==="male"?"Male":"Female"} · {AGE_BRACKETS.find(a=>a.id===age)?.label} · Run {fmt(result.km)}/km
        </div>
      </div>

      {/* Actual run pace — large input */}
      <div style={{ borderBottom:"1px solid #161616", padding:"22px 0" }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"#444", textTransform:"uppercase", marginBottom:8 }}>Actual 1km Run Pace</div>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SInput value={actualRunMin} onChange={setActualRunMin} placeholder="5"
            style={{ fontSize:34, width:60, textAlign:"left", padding:"0", background:"transparent", border:"none", color:actualRunMin?"#FFFFFF":"#282828", caretColor:"#F2620F", fontFamily:"'Space Mono',monospace", letterSpacing:"0.04em" }}
            inputMode="numeric"/>
          <span style={{ fontFamily:"'Space Mono',monospace", fontSize:34, color:actualRunMin?"#FFFFFF":"#282828", margin:"0 4px" }}>:</span>
          <SInput value={actualRunSec} onChange={setActualRunSec} placeholder="30"
            style={{ fontSize:34, width:60, textAlign:"left", padding:"0", background:"transparent", border:"none", color:actualRunSec?"#FFFFFF":"#282828", caretColor:"#F2620F", fontFamily:"'Space Mono',monospace", letterSpacing:"0.04em" }}
            inputMode="numeric"/>
        </div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#F1EFE8", opacity:0.3, letterSpacing:"0.08em", marginTop:6 }}>
          per km · from GPS watch
          {actualRunPace && result.km && (
            <span style={{ color: actualRunPace < result.km ? G.good : G.danger, marginLeft:10 }}>
              {actualRunPace < result.km ? `−${fmt(result.km - actualRunPace)} vs prediction` : `+${fmt(actualRunPace - result.km)} vs prediction`}
            </span>
          )}
        </div>
      </div>

      {/* Actual station times — orange bar + rows */}
      <div style={{ background:"#F2620F", padding:"11px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:"#000" }}>Actual Station Times</div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, fontWeight:600, letterSpacing:"0.12em", color:"#7A2A00" }}>Enter your splits</div>
      </div>
      <div style={{ marginBottom:0 }}>
        {STATIONS.map((s, i) => {
          const val = actuals[s.id]||"";
          const proj = result.stations[i];
          const actual = s.calc(val);
          const delta = actual != null ? actual - proj : null;
          return (
            <div key={s.id} style={{ display:"grid", gridTemplateColumns:"1fr auto auto", alignItems:"center", gap:10, padding:"13px 0", borderBottom:"1px solid #141414" }}>
              <div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:600, letterSpacing:"0.14em", textTransform:"uppercase", color:"#FFFFFF" }}>{s.name}</div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#333", letterSpacing:"0.1em", marginTop:3 }}>{s.inputUnit} · predicted: {fmt(proj)}</div>
              </div>
              <SInput value={val} onChange={v=>setActual(s.id,v)} placeholder={fmt(proj)} timeInput={s.inputUnit!=="rpm"} style={{ width:76, textAlign:"center", fontSize:15, borderColor: val ? "#F2620F" : "#242424" }}/>
              <div style={{ fontFamily:"'Space Mono',monospace", fontSize:13, textAlign:"right", minWidth:58, color: delta==null?"#282828":delta<0?G.good:delta>0?G.danger:"#888" }}>
                {delta==null ? "—" : delta<0 ? `−${fmt(Math.abs(delta))}` : delta>0 ? `+${fmt(delta)}` : "±0"}
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div onClick={analyzeRace} style={{ background:"#F2620F", padding:"22px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}>
        <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, fontWeight:700, letterSpacing:"0.22em", textTransform:"uppercase", color:"#FFFFFF" }}>Analyze My Race</span>
        <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:20, color:"#FFFFFF" }}>→</span>
      </div>

      {/* Debrief results */}
      {debriefResult && (() => {
        const { actualTotal, projectedTotal, accuracy, stationDeltas, runDelta } = debriefResult;
        const overallDelta = actualTotal - projectedTotal;
        const accColor = accuracy >= 95 ? G.good : accuracy >= 85 ? "#aadd00" : accuracy >= 70 ? G.warn : G.danger;
        return (
          <div style={{ marginTop:32 }}>
            {/* Actual Finish — large orange hero */}
            <div style={{ background:"#F2620F", padding:"22px 20px", marginBottom:0 }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, letterSpacing:"0.22em", color:"rgba(0,0,0,0.5)", textTransform:"uppercase", marginBottom:6 }}>Actual Finish</div>
              <div style={{ fontFamily:"'Space Mono',monospace", fontSize:38, color:"#FFFFFF", letterSpacing:"0.04em", lineHeight:1 }}>{fmtLong(actualTotal)}</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"rgba(0,0,0,0.5)", letterSpacing:"0.08em", marginTop:6 }}>
                {overallDelta <= 0 ? `−${fmt(Math.abs(overallDelta))} faster than predicted` : `+${fmt(overallDelta)} slower than predicted`}
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1px 1fr", marginBottom:0, borderBottom:"1px solid #161616" }}>
              <div style={{ padding:"16px 0 16px", paddingRight:16 }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.22em", textTransform:"uppercase", marginBottom:6 }}>Accuracy</div>
                <div style={{ fontFamily:"'Space Mono',monospace", fontSize:22, color:accColor }}>{accuracy}%</div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#333", letterSpacing:"0.08em", marginTop:3 }}>{accuracy>=95?"Near perfect":"Refine your benchmarks"}</div>
              </div>
              <div style={{ background:"#161616" }}/>
              <div style={{ padding:"16px 0 16px", paddingLeft:16 }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.22em", textTransform:"uppercase", marginBottom:6 }}>Run Delta</div>
                <div style={{ fontFamily:"'Space Mono',monospace", fontSize:22, color:runDelta==null?"#282828":runDelta<0?G.good:G.danger }}>
                  {runDelta==null ? "—" : runDelta<0 ? `−${fmt(Math.abs(runDelta))}` : `+${fmt(runDelta)}`}
                </div>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#333", letterSpacing:"0.08em", marginTop:3 }}>{runDelta==null?"no run entered":runDelta<0?"faster than pred.":"slower than pred."}</div>
              </div>
            </div>

            {/* Station comparison — orange bar + flat rows */}
            <div style={{ background:"#F2620F", padding:"11px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:13, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:"#000" }}>Station Breakdown</div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, fontWeight:600, letterSpacing:"0.12em", color:"#7A2A00" }}>Predicted vs Actual</div>
            </div>
            <div style={{ marginBottom:20 }}>
              {stationDeltas.map(({s, predicted, actual, delta}) => {
                const hasActual = actual != null;
                const deltaColor = !hasActual ? "#282828" : delta < -10 ? G.good : delta > 10 ? G.danger : "#888";
                return (
                  <div key={s.id} style={{ display:"grid", gridTemplateColumns:"1fr auto auto auto", alignItems:"center", gap:10, padding:"13px 0", borderBottom:"1px solid #141414" }}>
                    <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:600, fontSize:13, letterSpacing:"0.14em", textTransform:"uppercase", color:"#FFFFFF" }}>{s.name}</div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#2E2E2E", letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:2 }}>Pred</div>
                      <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:"#2E2E2E" }}>{fmt(predicted)}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:2 }}>Actual</div>
                      <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:hasActual?"#FFFFFF":"#282828" }}>{hasActual ? fmt(actual) : "—"}</div>
                    </div>
                    <div style={{ textAlign:"right", minWidth:52 }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:"#444", letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:2 }}>Delta</div>
                      <div style={{ fontFamily:"'Space Mono',monospace", fontSize:12, color:deltaColor }}>
                        {!hasActual ? "—" : delta===0 ? "±0" : delta<0 ? `−${fmt(Math.abs(delta))}` : `+${fmt(delta)}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Insights */}
            {(() => {
              const entered = stationDeltas.filter(d=>d.actual!=null);
              const overperformed = entered.filter(d=>d.delta<-15).sort((a,b)=>a.delta-b.delta);
              const underperformed = entered.filter(d=>d.delta>15).sort((a,b)=>b.delta-a.delta);
              if (!entered.length) return null;
              return (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:2 }}>
                  {overperformed[0] && (
                    <div style={{ background:`${G.good}08`, border:`1px solid ${G.good}22`, padding:"18px 22px" }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.good, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:8 }}>Best Station</div>
                      <div style={{ fontFamily:"'Anton',sans-serif", fontSize:20, color:"#FFFFFF", marginBottom:4, letterSpacing:"0.04em" }}>{overperformed[0].s.name}</div>
                      <div style={{ fontSize:13, color:"#ddd", fontFamily:"'Oswald',sans-serif" }}>
                        {fmt(Math.abs(overperformed[0].delta))} faster than predicted. This is a strength — protect it.
                      </div>
                    </div>
                  )}
                  {underperformed[0] && (
                    <div style={{ background:`${G.danger}08`, border:`1px solid ${G.danger}22`, padding:"18px 22px" }}>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, color:G.danger, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:8 }}>Focus Station</div>
                      <div style={{ fontFamily:"'Anton',sans-serif", fontSize:20, color:"#FFFFFF", marginBottom:4, letterSpacing:"0.04em" }}>{underperformed[0].s.name}</div>
                      <div style={{ fontSize:13, color:"#ddd", fontFamily:"'Oswald',sans-serif" }}>
                        {fmt(underperformed[0].delta)} slower than predicted. Update your benchmark and prioritize in training.
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })()}
    </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function SplitRox() {
  const [tab, setTab] = useState("onboard");
  const [onboarded, setOnboarded] = useState(false);
  const [initProfile, setInitProfile] = useState(null);
  const [highlightStation, setHighlightStation] = useState(null);
  const [entries, setEntries] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEntries().then(e => {
      setEntries(e);
      if (e.length > 0) {
        setLastResult(e[e.length-1]);
        // Returning user — skip onboarding
        setOnboarded(true);
        setTab("sim");
      }
      setLoading(false);
    });
  }, []);

  const handleOnboardComplete = useCallback((profile) => {
    setInitProfile(profile);
    setOnboarded(true);
    setTab("sim");
  }, []);

  const handleSave = useCallback((entry) => {
    setEntries(prev => [...prev, entry]);
    setLastResult(entry);
  }, []);

  if (loading) return (
    <div style={{ background:"#1A1A1C", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ fontFamily:"'Anton',sans-serif", fontSize:22, letterSpacing:"0.04em", display:"flex", alignItems:"baseline" }}>
        <span style={{ color:"#F2620F" }}>SPLIT</span>
        <span style={{ color:"#F2620F", fontSize:"0.72em", verticalAlign:"0.08em" }}>:</span>
        <span style={{ color:"#fff" }}>ROX</span>
      </div>
    </div>
  );

  if (!onboarded) return (
    <div style={{ minHeight:"100vh" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Anton&family=Oswald:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap');*{box-sizing:border-box;margin:0;padding:0;}body{background:#1C1C1E;}input,button{border-radius:2px;}::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-track{background:#111;}::-webkit-scrollbar-thumb{background:#333;}input::placeholder{color:#3A3A3C;}@media(max-width:600px){.sr-grid-2{grid-template-columns:1fr!important;}.sr-grid-3{grid-template-columns:1fr!important;}.sr-run-grid{grid-template-columns:repeat(2,1fr)!important;}.sr-hide-mobile{display:none!important;}}`}</style>
      <OnboardingFlow onComplete={handleOnboardComplete} />
    </div>
  );

  return (
    <div style={{ minHeight:"100vh" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Anton&family=Oswald:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap');*{box-sizing:border-box;margin:0;padding:0;}body{background:#1C1C1E;}input,button{border-radius:2px;}::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-track{background:#111;}::-webkit-scrollbar-thumb{background:#333;}input::placeholder{color:#3A3A3C;}@media(max-width:600px){.sr-grid-2{grid-template-columns:1fr!important;}.sr-grid-3{grid-template-columns:1fr!important;}.sr-run-grid{grid-template-columns:repeat(2,1fr)!important;}.sr-hide-mobile{display:none!important;}}`}</style>
      <Nav tab={tab} setTab={setTab} />
      {tab==="sim" && <SimulatorTab onSave={handleSave} initProfile={initProfile} onNavigate={(t,stationId)=>{ setHighlightStation(stationId||null); setTab(t); }} />}
      {tab==="train" && <TrainingTab lastResult={lastResult} highlightStation={highlightStation} onClearHighlight={()=>setHighlightStation(null)} onNavigate={(t)=>setTab(t)} />}
      {tab==="debrief" && <DebriefTab lastResult={lastResult} />}
      {tab==="progress" && <ProgressTab entries={entries} setEntries={setEntries} onNavigate={(t)=>setTab(t)} />}
    </div>
  );
}
